/* ═══════════════════════════════════════════════════════════════════════════
   Local adapter
   Answers the data contract from JSON and localStorage. Replaced by
   chamcore.js.

   It deliberately does a little more than a mock: it enforces the same rules
   the backend has to enforce — phone number required, a time in the past
   refused, no record without a customer relation. Otherwise the UI would be
   built on a kinder world than the one it has to live in.
   ═══════════════════════════════════════════════════════════════════════ */

import { BUSINESSES, STAFF, bySlug } from '../data/catalogue.js';
import { peopleAt } from '../data/people.js';
import { productsFor, product, retail, intervalFor } from '../data/products.js';
import { INDUSTRIES, industry, words, picksPerson, sectionsFor } from '../industries/index.js';
import {
  byDay as revenueByDay, byPerson as revenueByPerson, takings as revenueTakings,
} from '../domain/revenue.js';
import { imageFor, pack, urlOf } from '../images/index.js';

import { valid, normalise, error as phoneError } from '../domain/phone.js';
import { slots, takenDemo, isPast, shortDate, fromMinutes, toMinutes } from '../domain/time.js';
import { disclosedTo, mayDisclose } from '../domain/disclosure.js';
import {
  apply as applyAuth, canCancelFree, hasExpired, FREE_HOURS,
} from '../domain/authorisation.js';
import { fromAuthorisation, levelOf } from '../domain/identity.js';
import {
  channels, channelWords, confirmation, isPush, opensCheckout, emailError,
  payLink, linkExpiry,
} from '../domain/checkout.js';
import {
  kindOf, canSee, grant as grantAccess, revoke as revokeAccess,
  transfer as transferAccess, find as findDocs, seenBy as liveAccess,
} from '../domain/dataroom.js';
import {
  clean as cleanSignup, problems as signupProblems, missing as signupMissing,
  orgError,
} from '../domain/signup.js';
import {
  hasPlan, tablesAt, zonesAt, sittings, sittingWords, turnMinutes,
  largestParty, assign as assignTable, freeFor, tableWords, refusal as tableRefusal,
} from '../domain/tables.js';
import {
  hasMenu, menuIntro, groupsAt as menuGroups, lineById as menuLine,
} from '../domain/menu.js';
import { receipt as buildReceipt, summary as receiptSummary } from '../domain/receipt.js';
import { checkFile } from '../domain/media.js';
import {
  BASE as BRREG_BASE, queryFor as brregQuery, entitiesIn as brregEntities,
  readEntity, readRoles,
} from '../domain/brreg.js';
import {
  selfAllowed, trimSelf, needsApproval, notice as noticeFor,
} from '../domain/leave.js';
import {
  duty as occDuty, usable as occUsable, bookable as occBookable,
  employerView as occEmployerView, myView as occMyView,
} from '../domain/occhealth.js';
import { provider as occProvider } from '../data/health.js';
import { generate as rotaGenerate } from '../domain/roster.js';
import { reference as salesRef } from '../domain/salesdoc.js';
import {
  BUSINESS as DEMO_SALON, CUSTOMERS as demoCustomers,
  AHEAD as demoAhead, BEHIND as demoBehind,
} from '../data/demo-lykke.js';
import { journalFor, journalByDay } from '../domain/journal.js';
import {
  emptySession, keyOf as sessionKey, join as joinTable, mayChooseMode,
  order as placeOrder, advance as advanceOrder, settle as settleSession,
  split as splitBill, totalOf, billLines, needingWaiter, isOpen,
} from '../domain/tablesession.js';
import {
  RAILS, MODES, rail as railDef, stripSecrets, problems as railProblems,
} from '../domain/payments.js';
import {
  checkoutLink, portalLink, usable as subUsable,
  plan as planOf, order as billOrder,
} from '../domain/billing.js';

/* Reserved words. A tail URL and a platform route cannot both win, and a slug
   that moves breaks every link and QR code already printed — so the list is
   here, next to the only function that hands slugs out. */
export const RESERVED = ['admin', 'api', 'avtale', 'business', 'customer', 'login',
                         'platform', 'staff', 'website', 'assets', 'static', 'index'];

const KEY = 'avtale_customer_v1';

/* An artificial delay. A network exists, and the UI has to cope with the fact
   that it exists. */
const wait = (v, ms = 60) => new Promise(r => setTimeout(() => r(v), ms));

/* A refusal, as a rejected promise rather than a synchronous throw. The
   difference matters to the caller: a function that sometimes returns a
   promise and sometimes throws before returning one cannot be handled with a
   single `.catch`, and every screen here handles refusals that way. The
   sentence carried is the one the person reads — nothing translates it on
   the way out. */
const fail = (why, ms = 40) =>
  new Promise((_, no) => setTimeout(() => no(new Error(why)), ms));

function read() {
  try {
    return JSON.parse(localStorage.getItem(KEY) || '{}');
  } catch {
    return {};
  }
}
function write(s) {
  try {
    localStorage.setItem(KEY, JSON.stringify(s));
  } catch {
    /* private mode — the app must still work, only without a memory */
  }
}
function empty() {
  return { customers: {}, relations: {}, records: {}, disclosures: {}, businesses: {}, subscriptions: {}, auths: {} };
}
function state() {
  return Object.assign(empty(), read());
}

/* ── Catalogue ──────────────────────────────────────────────────────────── */

/* A business as it now stands: the catalogue row with whatever the tenant has
   since edited on top. Every read goes through this, because a slug the
   tenant changed and a slug the catalogue still remembers are the same
   business, and showing two different answers for it is how a saved edit
   appears to vanish on reload. */
function merged(b) {
  if (!b) return null;
  const edits = (state().businesses || {})[b.id];
  return edits ? { ...b, ...edits } : b;
}

/* ── The catalogue, plus whoever has signed up ────────────────────────────

   `BUSINESSES` is the seeded list and it is a constant. A tenant who signs
   up through the form is a row this browser made, kept in `s.tenants`, and
   every read has to see both — otherwise a business can sign up, be told it
   worked, and then not exist anywhere on the platform. That was the first
   thing the sign-up screen got wrong, and it looked exactly like a backend
   failure.

   Seeded first so their ids and slugs win a collision: a new tenant cannot
   take an address the catalogue already uses, and `slugError` refuses it at
   the door anyway. */
function tenants() {
  const s = state();
  return Object.values(s.tenants || {});
}

/** Everything the platform holds, seeded and signed-up alike. */
const catalogue = () => [...BUSINESSES, ...tenants()];

/**
 * The catalogue.
 *
 * `opts.all` is the tenant's own view — the console's business picker, which
 * must obviously list a business whose page is not up yet, since putting it
 * up is what the console is for. Everything else gets the PUBLIC catalogue,
 * and the difference is not cosmetic: a marketplace card for a business with
 * no address, no hours and nothing to book is a card that leads to "no page
 * here", which reads as a broken platform rather than as a business that
 * signed up ten seconds ago. The first walk through the sign-up form landed
 * exactly there.
 *
 * ── `opts.mine`: whose console this is ──────────────────────────────────
 *
 * The console's picker listed all thirty-six businesses in the catalogue,
 * so somebody who signed up thirty seconds earlier opened their console and
 * was offered a drop-down of thirty-five strangers' salons with their own
 * business somewhere in the middle. That is wrong twice over: it is not
 * their business, and on the real backend it would be somebody else's data.
 *
 * The rule is simple and it is the one Chamcore will enforce for real: a
 * console shows the businesses this account administers. In this build the
 * account is the browser, and the businesses it administers are the tenants
 * it created — `tenants()`.
 *
 * The fallback is deliberate. A browser that has created NOTHING is a
 * visitor looking at the prototype, and an empty console with no way in is a
 * worse demonstration than the catalogue; so that browser, and only that
 * browser, gets the demonstration businesses. The moment it creates one of
 * its own, the strangers disappear and do not come back.
 */
export const fetchBusinesses = (industry, opts = {}) => {
  const own = tenants();
  const from = opts.mine && own.length ? own : catalogue();
  const rows = industry ? from.filter(b => b.industry === industry) : from;
  const seen = rows.map(merged);
  return wait(opts.all ? seen : seen.filter(b => b.visible !== false && complete(b)));
};
export const fetchBusiness = id => wait(merged(catalogue().find(b => b.id === id)) || null);
/* The full person, not `{name, role}` — the same records the business's own
   page and the staff surface read, so a name on an appointment and a name on
   a profile are the same person. */
export const fetchStaff = businessId => wait(peopleAt(businessId));

/* ── What a business sells ───────────────────────────────────────────────
   The range belongs to the trade, not to the business — a salon sells salon
   products. A tenant's own catalogue replaces this list when Chamcore is
   connected; until then every item is marked `demo: true` so nothing
   downstream can present a demonstration price as a real one. */
export function fetchProducts(businessId) {
  const business = merged(catalogue().find(b => b.id === businessId));
  if (!business) return wait({ retail: null, items: [] });
  /* The tenant's own settings on top of the trade's range: which items they
     actually stock, and what they charge. The range itself belongs to the
     trade — a salon sells salon products — but the price and the shelf are
     the business's, and a platform that decided either would be setting
     somebody else's prices. */
  const own = business.shop || {};
  return wait({
    retail: retail(business.industry),
    items: productsFor(business.industry)
      .map(p => {
        const mine = own[p.id] || {};
        return {
          ...p,
          price: mine.price != null ? mine.price : p.price,
          stocked: mine.stocked !== false,
          /* `demo` goes off the moment the tenant has set a price of their
             own: it is then their figure, not the platform's example. */
          demo: mine.price == null,
          industry: business.industry,
          businessId,
        };
      })
      .filter(p => p.stocked),
  });
}

/** Everything the trade sells, stocked or not — the tenant's own shelf. */
export function fetchShop(businessId) {
  const business = merged(catalogue().find(b => b.id === businessId));
  if (!business) return wait({ retail: null, items: [] });
  const own = business.shop || {};
  return wait({
    retail: retail(business.industry),
    items: productsFor(business.industry).map(p => {
      const mine = own[p.id] || {};
      return {
        ...p,
        listPrice: p.price,
        price: mine.price != null ? mine.price : p.price,
        stocked: mine.stocked !== false,
        own: mine.price != null,
      };
    }),
  });
}

/* ── The demonstration day ────────────────────────────────────────────────
   One function, two readers. The calendar draws its grid from `fetchDiary`
   and the booking dialog asks `fetchAvailability` what is free, and until
   these shared a source they disagreed: the calendar showed Marte free at ten
   and the dialog refused ten, because one subtracted the business's busy
   periods and the other assigned them to people. A receptionist who presses a
   gap and is told it is taken stops trusting the screen.

   So the demo day is computed once, with a person attached to each period,
   and both readers take it from here. It goes away entirely when Chamcore
   answers with a real calendar. */
function demoDay(businessId, dateISO) {
  const roster = peopleAt(businessId);
  return takenDemo(businessId + '|' + dateISO).map(([from, to], i) => ({
    from, to,
    person: roster[i % Math.max(roster.length, 1)] || null,
  }));
}

/* ── A restaurant's evening ───────────────────────────────────────────────

   A table is not a slot in a working day and a restaurant's free times do not
   come from its opening hours. A place that runs two sittings sells the room
   twice at fixed times; a place that turns tables over seats until a last
   entry and no later. A kitchen open to eleven with a 165-minute turn stops
   seating at half past eight, and offering ten because the door is open is
   how a table is promised that cannot be given.

   So this answers a different question from `fetchAvailability`, and the two
   are separate functions rather than one with a flag: they are not the same
   question asked about different trades. */

/** Everything already reserved at a sitting, as the assignment wants it. */
function reservedAt(s, businessId, dateISO, sitting) {
  const out = [];
  Object.values(s.records).forEach(rows => rows.forEach(r => {
    if (r.businessId !== businessId) return;
    if (r.status === 'cancelled') return;
    if (String(r.time || '') !== `${dateISO} ${sitting}`) return;
    if (r.meta?.tables?.length) out.push({ tables: r.meta.tables });
  }));
  return out;
}

/**
 * The sittings tonight, each with how many parties of this size are left.
 *
 * The count is per party size on purpose: "3 tables free" is a different
 * answer for two people and for eight, and a place with nothing but twos left
 * is full for a party of six however many tables are empty.
 */
export function fetchSittings(businessId, dateISO, guests = 2, zone = null) {
  if (!hasPlan(businessId)) return wait(null);
  const s = state();
  const most = largestParty(businessId);
  return wait({
    zones: zonesAt(businessId),
    largest: most,
    words: sittingWords(businessId),
    turn: turnMinutes(businessId),
    /* A party bigger than the house is told so once, here, rather than after
       trying every evening this week. */
    tooLarge: guests > most,
    sittings: sittings(businessId).map(time => {
      const booked = reservedAt(s, businessId, dateISO, time);
      return {
        time,
        free: freeFor(businessId, { guests, booked, zone }),
        past: isPast(dateISO, time),
      };
    }),
  });
}

export function fetchAvailability(businessId, dateISO, duration = 45, staffId) {
  /* The business's own opening hours, not a constant. Every record in the
     catalogue publishes them and nothing read them until now — a restaurant
     open ten to eight was offering a table at 08:30. A day it is shut comes
     back empty, which the booking screen now says out loud. */
  const b = merged(catalogue().find(x => x.id === businessId));
  const day = demoDay(businessId, dateISO);

  /* Asked about one person, only that person's periods block. Asked about the
     business, all of them do — which is the right answer for a customer who
     has not chosen anybody. */
  const mine = staffId ? day.filter(x => x.person?.id === staffId) : day;

  /* And the real bookings, which block whoever they are with. */
  const s = state();
  const booked = [];
  Object.values(s.records).forEach(rows => rows.forEach(r => {
    if (r.businessId !== businessId) return;
    if (!String(r.time || '').startsWith(dateISO)) return;
    if (staffId && r.meta?.staff_id && r.meta.staff_id !== staffId) return;
    const start = toMinutes(String(r.time).slice(11, 16));
    if (start == null) return;
    const m = String(r.meta?.duration || '').match(/(\d+)\s*min/);
    booked.push([start, start + (m ? +m[1] : 45)]);
  }));

  return wait(
    slots({
      dateISO,
      duration,
      taken: [...mine.map(x => [x.from, x.to]), ...booked],
      hours: b?.opening_hours,
    })
  );
}

/* ── Booking ────────────────────────────────────────────────────────────── */
let counter = 0;
const newId = p => p + Date.now().toString(36) + (counter++).toString(36);

export async function confirmBooking(b) {
  /* The same checks the backend has to make. The client can lie. */
  const f = phoneError(b.phone);
  if (f) throw new Error(f);
  if (isPast(b.dateISO, b.time))
    throw new Error('That time has passed and cannot be booked.');

  const business = catalogue().find(x => x.id === b.businessId);
  if (!business) throw new Error('Unknown salon.');

  const uid = normalise(b.phone);
  const s = state();

  /* The customer relation comes into being here, and not before. */
  s.relations[uid] = s.relations[uid] || [];
  let rel = s.relations[uid].find(r => r.businessId === b.businessId);
  if (!rel) {
    rel = {
      businessId: b.businessId,
      industry: business.industry,
      since: new Date().toISOString().slice(0, 10),
      number: new Date().getFullYear() + '-' + (1000 + s.relations[uid].length * 7 + 3),
      by: 'customer',
    };
    s.relations[uid].push(rel);
  }
  /* A person the business had already typed into its own book has now booked
     through Avtale themselves. The relation stops being the business's note
     about somebody and becomes the customer's own — which is what entitles
     the business to see the account behind it. */
  rel.by = 'customer';

  const sv = business.services.find(t => t.name === b.service) || business.services[0];

  /* How the hold is authorised. Vipps rings the customer; a card cannot be
     rung, so it goes by a payment link to Stripe's own page. The screen has
     already chosen by the time this is called — this is where the choice is
     recorded, so that the cancellation screen weeks later knows which one to
     release and the receipt knows what to say. */
  const method = b.method === 'card' ? 'card' : 'vipps';
  const email = String(b.email || '').trim() || null;

  /* An address is never required — Checkout opens in front of the customer
     and the confirmation goes by SMS — but a broken one is caught, because a
     confirmation silently going nowhere is how somebody concludes the
     booking was never made. */
  const badEmail = emailError(email);
  if (badEmail) throw new Error(badEmail);

  /* Who is going to do it. The booking may name a person — `b.staff` — and
     when it does not, one is assigned from the roster rather than left blank.
     "Klipp & styling · Emilie" is the prototype's own line, and an
     appointment that cannot say who it is with is an appointment with a
     building. */
  const roster = peopleAt(business.id);
  /* Who it is with. A table is booked with a TABLE, not with a host — the
     roster is the floor staff and which of them walks over is the
     restaurant's business on the night, not a fact to print on a
     confirmation. Assigning one made "table for six" and "table for two" the
     same booking with a different name on it. */
  /* ── And who, where the customer engages the FIRM ──────────────────────
     You do not book the joiner. The firm sends whichever of their people is
     free that week — they know who is on which site, who has the van and who
     is certified for the work — so an appointment that names Per is a promise
     the platform made on the firm's behalf, and the firm finds out when the
     customer is standing in the kitchen asking where he is.

     What that forbids is the platform INVENTING an allocation, and the
     customer making one: on those trades nothing offers a person, so nothing
     arrives with one. It does not forbid the firm's own desk from allocating
     somebody deliberately, which is the whole of what "the firm decides"
     means — so a name that was explicitly supplied is kept.

     A table business takes neither. There is no host: see above. */
  const named = b.staff && roster.find(p => p.id === b.staff || p.name === b.staff);
  const person = hasPlan(business.id)
    ? null
    : named
      || (picksPerson(business.industry)
        ? roster[Math.abs(hashish(uid + b.dateISO + b.time)) % Math.max(roster.length, 1)] || null
        : null);

  /* ── The table ──────────────────────────────────────────────────────────
     A restaurant books a TABLE, and the table is the house's capacity rather
     than the guest's pick — see domain/tables.js. The guest said how many
     they are and, if they cared, which part of the room; the smallest table
     that holds them is assigned here, two neighbours are pushed together if
     no single one will do, and if the sitting is full the booking is refused
     rather than confirmed against a table that is not there.

     This is also where the old behaviour was plainly wrong: a restaurant's
     bookable resource was a HOST from the staff list, so a table for six and
     a table for two were the same booking with a different name on it. */
  let table = null;
  if (hasPlan(business.id)) {
    const guests = Math.max(1, Math.round(Number(b.guests) || 2));
    const booked = reservedAt(s, business.id, b.dateISO, b.time);
    table = assignTable(business.id, { guests, booked, zone: b.zone || null });
    if (!table) throw new Error(tableRefusal(business.id, guests, b.time));
    table.guests = guests;
  }

  const record = {
    id: newId('P'),
    businessId: b.businessId,
    type: 'agreement',
    title: words(business.industry).appointment_doc,
    subtitle: `${sv.name} · ${b.time}`,
    time: `${b.dateISO} ${b.time}`,
    date: shortDate(b.dateISO),
    ts: Date.now(),
    amount: sv.price || null,
    status: 'confirmed',
    meta: {
      service: sv.name,
      /* What was actually reserved. `tables` is what the assignment gave,
         `guests` is what the party said — both on the record, because the
         diary needs the first and the kitchen needs the second. */
      ...(table ? {
        tables: table.tables,
        guests: table.guests,
        zone: table.zone,
        joined: table.joined,
        seats: table.seats,
        table_words: tableWords(table),
      } : {}),
      /* The length actually booked, in minutes, not the text on the price
         list. A business that changes a colour from 90 to 120 minutes must
         not retroactively lengthen appointments already in the diary — the
         block belongs to the booking, so it is written onto it. */
      /* A table is held for the house's turn time, not for the length of a
         dish. Booking "Chef's menu · 2 t" against a 165-minute sitting and
         then freeing the table after two hours would sell it twice. */
      minutes: table ? turnMinutes(business.id) : (Number(b.duration) || sv.minutes || 45),
      duration: (table ? turnMinutes(business.id)
                       : (Number(b.duration) || sv.minutes || 45)) + ' min',
      payment: method === 'card' ? 'Card' : 'Vipps',
      staff: person ? person.name : null,
      staff_id: person ? person.id : null,
      staff_title: person ? person.title : null,
    },
    /* The hold. It is on the appointment record rather than in a table of its
       own because an authorisation without its appointment is meaningless and
       an appointment whose hold lives somewhere else is how the two drift
       apart. */
    auth: sv.price ? {
      id: newId('A'),
      state: 'reserved',
      amount: sv.price,
      /* The late-cancellation fee. Equal to the amount unless the tenant sets
         a lower one — and whatever it is, it is on the screen before the
         customer authorises. */
      fee: sv.price,
      hours: FREE_HOURS,
      created: new Date().toISOString(),
      method,
      /* No merchant keys: nothing is actually held. The state machine is
         real, the money is not, and every screen says so. */
      demo: true,
    } : null,
    body:
      `The appointment at ${business.name} is confirmed. This agreement is your ` +
      'copy, and it sits here together with the receipt and the messages.',
  };

  /* ── The confirmation ───────────────────────────────────────────────────
     Sent when the booking exists, and not a moment earlier. "Your appointment
     is confirmed" before anybody has authorised anything is a sentence that
     produces a no-show and an argument about who said what.

     Both channels carry the same sentences, from one function, because an SMS
     that quotes one deadline and an email that quotes another is worse than
     either alone. It is written onto the record rather than fired and
     forgotten, so the customer's own page can show them what they were sent —
     "I never got a confirmation" is answered by showing it. */
  const msg = confirmation({
    business: business.name,
    service: sv.name,
    when: `${shortDate(b.dateISO)} at ${b.time}`,
    person: person?.name || null,
    amount: sv.price || null,
    free: sv.price ? `up to ${FREE_HOURS} hours before` : null,
  });
  const to = { phone: uid, email };

  /* The Checkout session, when the card road was taken. Nothing is sent
     anywhere: the session opened in front of the customer and they
     authorised on it. What is kept is that it existed and when it would have
     run out, so a session opened and abandoned can be swept and the slot let
     go rather than held for ever. */
  record.checkout = (sv.price && opensCheckout(method))
    ? {
        url: payLink(`${business.id}-${b.dateISO}-${b.time}`),
        expires: linkExpiry().toISOString(),
        demo: true,
      }
    : null;

  record.sent = {
    at: new Date().toISOString(),
    channels: channels(to),
    words: channelWords(to),
    subject: msg.subject,
    sms: msg.sms,
    body: msg.body,
    email,
    /* Nothing leaves the browser in this build: there is no SMS gateway and
       no mail server configured. The message is real, composed and stored;
       the sending is not, and every screen that shows it says so. */
    demo: true,
  };

  s.records[uid] = s.records[uid] || [];
  s.records[uid].unshift(record);
  seedHistory(s, uid, business);

  /* ── The account ────────────────────────────────────────────────────────
     Authorising a payment IS an identification. Vipps Userinfo hands back the
     name, the date of birth and the address, checked against Folkeregisteret;
     a card hands back a name. Either way the platform now knows who this is,
     and carrying on to ask them to "create an account" afterwards is asking a
     question we already have the answer to.

     So the account is made here, from whatever the method actually returned,
     and the customer lands signed in. Nothing is invented for the fields a
     method does not return — see domain/identity.js on why a card is not an
     identity check and must never be shown as one.

     An existing account is not overwritten by a weaker one: somebody who
     verified with Vipps last month and pays by card today is still verified,
     and downgrading them because of how they paid would be a regression in
     what the platform knows. */
  if (record.auth && b.identify !== false) {
    const who = method === 'vipps'
      ? VIPPS_DEMO
      : { name: VIPPS_DEMO.name };          // a card gives a name and no more
    const made = fromAuthorisation({ phone: uid, method, person: who });
    /* An email address the customer typed is theirs and is kept — it is the
       one field here that came from them rather than from a payment method,
       and it is how the confirmation reached them. */
    if (email) made.email = email;
    const had = s.customers[uid];
    const stronger = !had
      || ['none', 'self', 'card', 'vipps'].indexOf(levelOf(made))
         >= ['none', 'self', 'card', 'vipps'].indexOf(levelOf(had));
    s.customers[uid] = stronger ? { ...had, ...made } : had;
  }

  write(s);

  return wait({
    booking: b, relation: rel, record,
    customer: s.customers[uid] || null,
    sent: record.sent,
  });
}

/* A small stable hash, for assigning a person to a booking in a way that does
   not change between two reads of the same appointment. */
function hashish(str) {
  let h = 0x811c9dc5;
  for (let i = 0; i < str.length; i++) {
    h ^= str.charCodeAt(i);
    h = Math.imul(h, 0x01000193) >>> 0;
  }
  return h >>> 0;
}

/* ── Demonstration history ────────────────────────────────────────────────
   "Tidligere avtaler" needs something to show, and a customer who has just
   made their first booking has no past. So one is seeded — but only at the
   business they have just booked with, and never at any other.

   The first version of this function seeded three visits at three different
   businesses, and test_backbone.py caught it within a minute: "only the salon
   the customer actually used is in the mirror" went red. It was right to.
   Writing a relation to Harmony Spa for somebody who booked at Lykke tells
   that customer they are a customer of a business they have never been to,
   and it would have put their name in front of a tenant who has no claim to
   it. The rule the whole platform is built on — a business sees a customer
   only once that customer is theirs — does not get an exception for demo
   data.

   So: past visits at the business they chose, on the relation they created
   themselves, every record labelled `demo: true` and saying so on the screen.
   Each one carries the two things Stein asked for — who did it, and what was
   used. When Chamcore is connected, `fetchRecords` returns the real records
   and this never runs.  */
function seedHistory(s, uid, business) {
  s.records[uid] = s.records[uid] || [];
  if (s.records[uid].some(r => r.demo && r.businessId === business.id)) return;

  const ind = business.industry;
  const range = productsFor(ind);
  const roster = peopleAt(business.id);
  const services = business.services || [];
  if (!services.length) return;

  const back = days => {
    const d = new Date();
    d.setDate(d.getDate() - days);
    return d.toISOString().slice(0, 10);
  };

  /* Two visits, far enough apart to read as a history rather than a mistake.
     The products are the first two in the trade's range that can actually be
     subscribed to — a trade that sells nothing gets visits and no aftercare
     message, which is the correct outcome for a law firm. */
  const subscribable = range.filter(p => p.every).slice(0, 2).map(p => p.id);

  [{ ago: 41, si: 0, used: subscribable }, { ago: 96, si: 1, used: subscribable.slice(0, 1) }]
    .forEach(pick => {
      const sv = services[pick.si] || services[0];
      const person = roster[hashish(uid + business.id + pick.ago) % Math.max(roster.length, 1)] || null;
      const dateISO = back(pick.ago);
      const ts = new Date(dateISO + 'T12:00:00').getTime();

      s.records[uid].push({
        id: newId('V'),
        businessId: business.id,
        type: 'visit',
        title: words(ind).visit || 'Completed appointment',
        subtitle: `${sv.name}${person ? ' · ' + person.name : ''}`,
        time: `${dateISO} 14:00`,
        date: shortDate(dateISO),
        ts,
        amount: sv.price || null,
        status: 'done',
        demo: true,
        meta: {
          service: sv.name, duration: sv.unit, payment: 'Vipps',
          staff: person ? person.name : null,
          staff_id: person ? person.id : null,
          staff_title: person ? person.title : null,
          products: pick.used,
        },
        body: `${sv.name} at ${business.name}` +
              (person ? `, carried out by ${person.name}.` : '.'),
      });

      /* The message after it: who did it, and what was used on you. The
         prototype's own promise — "Vi oppgir alltid hvilke produkter som
         brukes" — and the thing a subscription hangs off. A trade with no
         range gets no message rather than an empty one. */
      if (!pick.used.length) return;
      s.records[uid].push({
        id: newId('A'),
        businessId: business.id,
        type: 'aftercare',
        title: 'After your appointment',
        subtitle: person ? `From ${person.name}` : business.name,
        time: `${dateISO} 16:30`,
        date: shortDate(dateISO),
        ts: ts + 9000000,
        status: 'sent',
        demo: true,
        meta: {
          staff: person ? person.name : null,
          staff_id: person ? person.id : null,
          staff_title: person ? person.title : null,
          service: sv.name,
          products: pick.used,
        },
        body:
          (person ? `${person.name.split(' ')[0]} here. ` : '') +
          'Thank you for coming in. This is what was used on you, so you know what to ' +
          'ask for — and you can have any of it sent to you before it runs out.',
      });
    });
}

/* ── The authorisation ────────────────────────────────────────────────────
   Three operations, and each of them moves somebody's money, so each of them
   re-reads the record and applies `domain/authorisation.js` rather than
   trusting what the caller thought the state was. The client can lie; the
   clock cannot.

   `cancelAppointment` is the customer's. `completeAppointment` is the
   business's. Neither may do the other's job — a business that could
   "cancel" a customer's booking free of charge after the deadline, or a
   customer who could mark their own appointment carried out, is a hole in the
   agreement rather than a convenience. */

function findRecord(uid, recordId) {
  const rows = state().records[uid] || [];
  return rows.find(r => r.id === recordId) || null;
}

/** The customer cancels. Before the deadline the hold is released; after it,
    the agreed fee stands. The answer says which, and why. */
export function cancelAppointment(phone, recordId) {
  const uid = normalise(phone);
  const s = state();
  const rows = s.records[uid] || [];
  const record = rows.find(r => r.id === recordId);
  if (!record) throw new Error('No such appointment.');
  if (record.status === 'cancelled') throw new Error('This one is already cancelled.');

  const now = new Date();
  const free = canCancelFree(record, now, record.auth?.hours);

  record.status = 'cancelled';
  record.cancelled = now.toISOString();
  if (record.auth && record.auth.state === 'reserved') {
    record.auth = applyAuth(record.auth, 'cancel', { record, now });
  }

  /* The customer keeps the record. An appointment that disappears when it is
     cancelled takes the evidence of the agreement with it — including what
     was charged and why. */
  write(s);
  return wait({ record, free, auth: record.auth || null });
}

/** The business marks it carried out, and the held amount is charged. */
export function completeAppointment(businessId, recordId) {
  const s = state();
  let found = null;
  Object.values(s.records).forEach(rows => rows.forEach(r => {
    if (r.id === recordId && r.businessId === businessId) found = r;
  }));
  if (!found) throw new Error('No such appointment at this business.');
  if (found.status === 'cancelled') throw new Error('This one was cancelled.');
  if (found.auth && found.auth.state === 'reserved') {
    found.auth = applyAuth(found.auth, 'complete', { record: found, now: new Date() });
  }
  found.status = 'done';
  found.type = 'visit';
  write(s);
  return wait({ record: found, auth: found.auth || null });
}

/** A hold that has lapsed on its own. Swept on read rather than by a timer:
    there is no server here to run one, and a state that is only correct while
    the tab is open is not a state. */
export function sweepAuthorisations(phone) {
  const uid = normalise(phone);
  const s = state();
  const rows = s.records[uid] || [];
  let changed = false;
  rows.forEach(r => {
    if (hasExpired(r.auth)) {
      r.auth = applyAuth(r.auth, 'expire', { record: r, now: new Date() });
      changed = true;
    }
  });
  if (changed) write(s);
  return wait(rows);
}

/* ── Subscriptions ───────────────────────────────────────────────────────
   A subscription is on ONE product, from ONE business, at an interval the
   customer sets. It belongs to the customer, not to the tenant: it is on My
   page, it is paused and cancelled from there, and cancelling it is one
   press with no retention screen in front of it. That is not a nicety —
   angrerettloven gives a fourteen-day right of withdrawal on a distance
   sale, and a subscription that is hard to leave is a subscription that
   breaks it. */
export function fetchSubscriptions(phone) {
  const uid = normalise(phone);
  const list = state().subscriptions[uid] || [];
  return wait(list.map(hydrate).filter(Boolean));
}

function hydrate(sub) {
  const p = product(sub.productId);
  const business = catalogue().find(b => b.id === sub.businessId);
  if (!p || !business) return null;
  return {
    ...sub,
    product: { ...p, demo: true },
    business: { id: business.id, name: business.name, industry: business.industry },
    next: sub.status === 'active' ? nextDue(sub) : null,
  };
}

/** When the next delivery falls due — the start plus whole intervals. */
function nextDue(sub) {
  const start = new Date(sub.started + 'T12:00:00');
  const step = sub.weeks * 7 * 86400000;
  let when = start.getTime();
  const now = Date.now();
  while (when <= now) when += step;
  return new Date(when).toISOString().slice(0, 10);
}

export function subscribe({ phone, businessId, productId, weeks }) {
  const uid = normalise(phone);
  if (!valid(uid)) throw new Error('Invalid mobile number.');
  const p = product(productId);
  if (!p) throw new Error('Unknown product.');
  if (!p.every) throw new Error('This one is bought once — there is nothing to subscribe to.');

  const s = state();
  s.subscriptions[uid] = s.subscriptions[uid] || [];
  const already = s.subscriptions[uid].find(
    x => x.productId === productId && x.businessId === businessId && x.status !== 'cancelled');
  if (already) throw new Error('You already have this one.');

  const sub = {
    id: newId('S'),
    businessId,
    productId,
    weeks: weeks || intervalFor(p.every).weeks,
    price: p.price,
    started: new Date().toISOString().slice(0, 10),
    status: 'active',
  };
  s.subscriptions[uid].push(sub);
  write(s);
  return wait(hydrate(sub));
}

export function updateSubscription(phone, id, patch) {
  const uid = normalise(phone);
  const s = state();
  const list = s.subscriptions[uid] || [];
  const sub = list.find(x => x.id === id);
  if (!sub) throw new Error('No such subscription.');
  /* Only these three may be changed from here. A price change is the
     tenant's, and it arrives with the catalogue, not through this call. */
  if (patch.weeks) sub.weeks = patch.weeks;
  if (patch.status) sub.status = patch.status;
  write(s);
  return wait(hydrate(sub));
}

/* ── The customer ───────────────────────────────────────────────────────── */
/** Who offers a given service at a business. An empty `staff` on the service
    means everybody — the right default for a salon where any chair can cut
    hair, and the thing a law firm overrides the moment two people do
    different work. */
export function whoOffers(businessId, serviceName) {
  const b = merged(catalogue().find(x => x.id === businessId));
  /* Nobody, where the customer does not choose the person. An empty list is
     the honest answer rather than a hidden picker: the booking screen asks
     this question to decide whether there is a choice to offer, and for a
     building firm there is not — they allocate. See `picksPerson`. */
  if (!picksPerson(b?.industry)) return wait([]);
  const roster = peopleAt(businessId);
  const sv = (b?.services || []).find(t => t.name === serviceName);
  if (!sv || !sv.staff?.length) return wait(roster);
  return wait(roster.filter(p => sv.staff.includes(p.id)));
}

export function fetchCustomer(phone) {
  const uid = normalise(phone);
  return wait(state().customers[uid] || null);
}

/* The demo answer from Vipps Userinfo. The person is the same one as in the
   prototype. The national identity number is not available in Norway — so it
   is not here either. */
const VIPPS_DEMO = {
  name: 'Anna Bergland',
  date_of_birth: '1991-04-18',
  email: 'anna.bergland@example.no',
  address: { street: 'Bygdøy allé 42', postal_code: '0265', city: 'Oslo' },
};

export async function verifyWithVipps(phone) {
  const uid = normalise(phone);
  if (!valid(uid)) throw new Error('Invalid mobile number.');
  const s = state();
  const c = {
    uid,
    phone: uid,
    ...VIPPS_DEMO,
    source: 'vipps',
    verified: true,
    created: new Date().toISOString(),
  };
  s.customers[uid] = c;
  write(s);
  /* The push takes time in the real world. */
  return wait(c, 1400);
}

export async function createCustomerManually(d) {
  const uid = normalise(d.phone);
  if (!valid(uid)) throw new Error('Invalid mobile number.');
  if (String(d.name || '').trim().split(/\s+/).filter(Boolean).length < 2)
    throw new Error('We need both a first name and a last name.');
  if (d.email && !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(d.email))
    throw new Error('That email address does not look right.');

  const s = state();
  const c = {
    uid,
    phone: uid,
    name: String(d.name).trim(),
    date_of_birth: d.date_of_birth || '',
    email: d.email || '',
    address: {
      street: d.street || '',
      postal_code: d.postal_code || '',
      city: d.city || '',
    },
    source: 'manual',
    verified: false,
    created: new Date().toISOString(),
  };
  s.customers[uid] = c;
  write(s);
  return wait(c);
}

/* ── The mirror ─────────────────────────────────────────────────────────── */
export function fetchRelations(phone) {
  return wait(state().relations[normalise(phone)] || []);
}

export function fetchRecords(phone) {
  return wait(state().records[normalise(phone)] || []);
}

export function fetchDisclosure(phone, businessId) {
  const uid = normalise(phone);
  const s = state();
  const customer = s.customers[uid];
  const business = catalogue().find(b => b.id === businessId);
  const rel = s.relations[uid] || [];
  const fields = disclosedTo(customer, business, mayDisclose(customer, businessId, rel));
  if (!fields) return wait(null);

  /* Log that something was disclosed, and what. */
  s.disclosures[uid] = s.disclosures[uid] || {};
  s.disclosures[uid][businessId] = {
    date: new Date().toISOString().slice(0, 10),
    fields: Object.keys(fields),
  };
  write(s);
  return wait({ fields, log: s.disclosures[uid][businessId] });
}

/* ── The industry registry and the public page ───────────────────────────
   The registry is a table on the backend. Here it is the file it was written
   from, so the shape is identical and the swap is a one-line change. */

export const fetchIndustries = () => wait(INDUSTRIES);

/** A business is only reachable on the public web once it has something to
    show. Half-filled businesses make the whole platform look broken, so the
    gate is here as well as on the server. */
export function complete(b) {
  if (!b || !b.name) return false;
  /* One rule, in domain/signup.js, so the gate on the public page and the
     checklist the tenant is shown in the console cannot disagree about what
     "ready" means. They did: this function wanted staff and said nothing
     about the org.no, while the console asked for the org.no and said
     nothing about staff, so a tenant could tick everything they were shown
     and still have no page. */
  const short = signupMissing({ ...b, staff: peopleAt(b.id) });

  /* ── ONE EXCEPTION, AND IT HAS TO COME OUT ─────────────────────────────
     A row already marked `demo: true` may be published without an
     organisation number, and only without that — every other thing on the
     checklist still has to be there.

     Why it exists: the demonstration catalogue is the only way to look at a
     trade before a real business has joined it, and the alternative was to
     generate a number. A string that fails mod-11 was issued to nobody and
     one that passes belongs to somebody real, so neither is available. The
     page says "Organisation number not set" rather than printing digits —
     see section/contact.jsx — which is the honest version of the same fact.

     Why it must not survive: a gate with an exception is a gate with an
     exception. Nothing in production is `demo: true`, so this is inert the
     moment Chamcore answers, but inert is not gone. Delete this branch and
     the `demo` flag on the row when the catalogue is real. There is a test
     that pins the exception to exactly this shape so it cannot widen
     quietly. */
  if (b.demo && short.length === 1 && short[0].id === 'org_no') return true;

  return short.length === 0;
}

export function fetchWebsite(slug) {
  /* A business answers at one address, and only one. The slug the tenant
     chose wins over the one the catalogue was born with — and the old one
     stops working the moment it is replaced, because a slug that quietly
     answers at two addresses is how two versions of a page end up indexed.
     That is the whole reason moving a slug is a decision and not a setting. */
  const s = state();
  const edits = s.businesses || {};
  const renamed = Object.entries(edits).find(([, e]) => e.slug === slug);
  /* `bySlug` only knows the seeded catalogue, so a business that signed up
     through the form answered nowhere — its own page 404'd the moment it was
     created. The runtime catalogue is what has to be searched. */
  const born = catalogue().find(b => b.slug === slug || b.id === slug) || null;
  const business = merged(
    renamed ? catalogue().find(b => b.id === renamed[0])
            : born && !edits[born.id]?.slug ? born
            : null
  );
  if (!business || business.visible === false || !complete(business)) return wait(null);

  const ind = industry(business.industry);
  /* The staff list is the full person record now, not `{name, role}`. `role`
     is kept alongside `title` so that everything already reading it — the
     JSON-LD below, the diary, the prerender — keeps working while the pages
     that want the whole person can have it. A customer chooses the person as
     often as the business, and until this change there was nothing to choose
     on. */
  const staff = peopleAt(business.id).map(p => ({ ...p, role: p.title }));
  return wait({
    business,
    industry: {
      code: ind.code, name: ind.name, layout: ind.layout, theme: ind.theme,
      /* `sectionsFor`, not `ind.sections`. A trade where the firm allocates
         the work — or a restaurant, which gives you a table rather than a
         waiter — does not get a band of named people on its public page. The
         rule is applied at the seam so no renderer has to remember it. */
      words: ind.words, sections: sectionsFor(ind.code), price_visible: ind.price_visible,
      standard: ind.standard, rights: ind.rights, papers: ind.papers,
    },
    services: business.services,
    /* ── The card itself, on the public page ───────────────────────────────
       There were two menus. The band on a restaurant's page rendered
       `services` — four lines: a table reservation, a chef's menu, takeaway
       and a room for parties — and called that the menu. The twenty-six
       dishes with their allergens and diets existed only behind the code on
       the table, where nobody deciding whether to come has ever looked.

       So the card travels with the page. It is the same list the table orders
       from, which is the point: a guest who reads it at home and a guest who
       reads it at the table are reading one menu, and the one they read at
       home is in the prerendered HTML where a search engine finds it. Null
       for a business that keeps no card — every other trade. */
    menu: hasMenu(business.id)
      ? { intro: menuIntro(business.id), groups: menuGroups(business.id) }
      : null,
    staff,
    /* Resolved here rather than in the view, so a prerendered page, a live
       page and the marketplace card all show the same picture. */
    images: {
      hero: imageFor(business, 'hero'),
      card: imageFor(business, 'card'),
    },
    seo: seo(business, ind, staff),
  });
}

/** schema.org LocalBusiness. Free to produce once the data is already there,
    and it is what puts the business in a map result. */
function seo(b, ind, staff) {
  const [street, rest] = String(b.street || '').split(/,\s*/);
  const [postal, ...city] = String(rest || '').split(' ');
  return {
    title: `${b.name} — ${ind.name} in ${b.city} | Avtale.net`,
    description: (b.tagline || '').slice(0, 155),
    jsonld: {
      '@context': 'https://schema.org',
      '@type': 'LocalBusiness',
      name: b.name,
      description: b.tagline,
      telephone: b.phone,
      email: b.email,
      address: {
        '@type': 'PostalAddress',
        streetAddress: street || b.street,
        postalCode: postal || '',
        addressLocality: city.join(' ') || b.city,
        addressCountry: 'NO',
      },
      openingHours: b.opening_hours,
      aggregateRating: b.rating
        ? { '@type': 'AggregateRating', ratingValue: b.rating, reviewCount: b.reviews }
        : undefined,
      employee: staff.map(a => ({ '@type': 'Person', name: a.name, jobTitle: a.role })),
      makesOffer: (b.services || []).map(t => ({
        '@type': 'Offer',
        itemOffered: { '@type': 'Service', name: t.name, description: t.description },
        price: ind.price_visible && t.price ? t.price : undefined,
        priceCurrency: ind.price_visible && t.price ? 'NOK' : undefined,
      })),
    },
  };
}

/* ── The business's own side ─────────────────────────────────────────────
   The rule that matters here is the one that runs the other way: a business
   sees its own customers and no one else's, and of those only the fields its
   industry is allowed to be told. `disclosedTo` is the same function the
   customer's own surface uses, which is the point — one gate, both
   directions. */

/* ── What the business took ──────────────────────────────────────────────
   Real bookings only. The demonstration day is deliberately absent: it
   exists so an empty browser shows a working diary, and a business that
   found the platform's sample day inside its own takings would be right to
   distrust every other number on the screen. See domain/revenue.js on why
   agreed, held and taken are three figures and never one. */
export function fetchTakings(businessId, fromISO, toISO) {
  const s = state();
  seedSalon(s, businessId);
  const rows = [];
  Object.values(s.records).forEach(list => list.forEach(r => {
    if (r.businessId !== businessId) return;
    /* An appointment or a completed visit. A receipt is the same money
       counted a second time, and a letter is not money at all. */
    if (!['agreement', 'visit', 'quote'].includes(r.type)) return;
    const day = String(r.time || '').slice(0, 10);
    if (!day || day < fromISO || day > toISO) return;
    rows.push({
      time: r.time,
      amount: r.amount ?? r.auth?.amount ?? 0,
      status: r.status || 'confirmed',
      staff: r.meta?.staff || '',
      staffId: r.meta?.staff_id || null,
      auth: r.auth || null,
      service: r.meta?.service || r.title,
      /* Both flags are carried, not assumed. This line said
         `source: 'avtale.net'` and dropped `demo` altogether, which quietly
         made the exclusion in domain/revenue.js unreachable: the seeded
         demonstration visits — real money, on purpose, so the customer's own
         history has something in it — were being added to the salon's
         income. The row says what it is; this function does not get to
         decide. */
      demo: !!r.demo,
      source: r.source || 'avtale.net',
    });
  }));
  /* What sold, and when. Three cuts the day list cannot give: which service
     is actually earning, which weekday is heavy, and which hours are dead.
     All three are counted here rather than in a component, because a
     component that received the rows would be one refactor away from
     counting them differently on two screens. */
  const live = rows.filter(r => !r.demo && r.status !== 'cancelled');

  const byService = {};
  live.forEach(r => {
    const k = r.service || '(not named)';
    const x = byService[k] || (byService[k] = { service: k, agreed: 0, count: 0 });
    x.agreed += Number(r.amount) || 0;
    x.count += 1;
  });

  /* Monday first, and every weekday kept even at nought — a Sunday missing
     from the list reads as "no data" rather than "shut", and those are
     different facts about a business. */
  const week = Array.from({ length: 7 }, (_, i) => ({ day: i, agreed: 0, count: 0 }));
  /* The opening hours decide the span rather than a fixed 00–23, so a salon
     does not get ten empty rows either end of its day. */
  const hourMap = {};
  live.forEach(r => {
    const d = new Date(String(r.time).slice(0, 10) + 'T12:00:00');
    const wd = (d.getDay() + 6) % 7;
    week[wd].agreed += Number(r.amount) || 0;
    week[wd].count += 1;
    const h = Number(String(r.time).slice(11, 13));
    if (Number.isFinite(h)) {
      const x = hourMap[h] || (hourMap[h] = { hour: h, agreed: 0, count: 0 });
      x.agreed += Number(r.amount) || 0;
      x.count += 1;
    }
  });
  const hours = Object.values(hourMap).sort((a, b) => a.hour - b.hour);

  return wait({
    from: fromISO,
    to: toISO,
    days: revenueByDay(rows, { from: fromISO, to: toISO }),
    people: revenueByPerson(rows, peopleAt(businessId)),
    services: Object.values(byService).sort((a, b) => b.agreed - a.agreed),
    week,
    hours,
    total: revenueTakings(rows),
  });
}

/* ── The journal ──────────────────────────────────────────────────────────
   The accountant's half of the takings. Only what was CAPTURED reaches it —
   see domain/journal.js on why an appointment with a price on it is not
   income — and only where the business has said which VAT rate the service
   carries. A rate this platform guessed would be a rate somebody filed a
   return on.

   The rate lives on the business's own price list, beside the price, because
   that is where somebody looking at a service expects to find it. */
export function fetchJournal(businessId, fromISO, toISO) {
  const s = state();
  seedSalon(s, businessId);
  const b = catalogue().find(x => x.id === businessId);
  if (!b) return fail('No such business.');
  const shown = merged(b) || b;
  const services = shown.services || [];
  /* The rates live under their OWN key, not under `services`. `merged()`
     spreads everything in `s.businesses[id]` over the business record, so a
     map stored as `services` would have replaced the actual price list with
     a lookup table — every service on the public page gone, and the cause
     three files away from the symptom. */
  const vatOf = (s.businesses?.[businessId]?.serviceVat) || {};

  const rows = [];
  Object.values(s.records).forEach(list => list.forEach(r => {
    if (r.businessId !== businessId) return;
    if (!['agreement', 'visit', 'quote'].includes(r.type)) return;
    const day = String(r.time || '').slice(0, 10);
    if (!day || day < fromISO || day > toISO) return;
    rows.push({
      time: r.time,
      amount: r.amount ?? r.auth?.amount ?? 0,
      status: r.status || 'confirmed',
      auth: r.auth || null,
      service: r.meta?.service || r.title,
      demo: !!r.demo,
      source: r.source || 'avtale.net',
    });
  }));

  const rateFor = row => vatOf[row.service] || null;
  const country = leaveOf(s, businessId).settings.country || b.country || 'NO';
  const days = journalByDay(rows, rateFor, country);

  return wait({
    from: fromISO, to: toISO,
    country,
    business: b.name,
    orgNo: shown.org_no || null,
    days,
    total: journalFor(rows, rateFor, country),
    /* Which services still have no rate. The business answers this once per
       service and then the export stops being a judgement call. */
    services: services.map(x => ({
      name: x.name, price: x.price ?? null, vat: vatOf[x.name] || null,
    })),
    systems: (s.businesses?.[businessId]?.accounting) || {},
  });
}

/** The VAT rate for one service. The business's own fact, stated once. */
export function saveServiceVat(businessId, serviceName, rateId) {
  const s = state();
  const b = catalogue().find(x => x.id === businessId);
  if (!b) return fail('No such business.');
  s.businesses = s.businesses || {};
  s.businesses[businessId] = s.businesses[businessId] || {};
  const own = s.businesses[businessId];
  own.serviceVat = own.serviceVat || {};
  if (rateId) own.serviceVat[serviceName] = rateId;
  else delete own.serviceVat[serviceName];
  write(s);
  return fetchJournal(businessId, '1970-01-01', '2999-12-31');
}

export function fetchDiary(businessId, dateISO, staffId) {
  const b = catalogue().find(x => x.id === businessId);
  if (!b) return wait([]);
  seedSalon(state(), businessId);
  /* `peopleAt`, not `STAFF`. These two lists are the same for most businesses
     and completely different for six of them: the nine advisers ported from
     the prototype REPLACE the generated roster at Hovden, Sandberg, Nordvik,
     Bakke, Vold and Ravn. The staff surface builds its picker from
     `peopleAt`, so a diary built from `STAFF` named people who were not in
     the picker, every row was filtered out, and the staff surface at those
     six businesses said "nothing is booked with you today" forever. One
     source of people, everywhere. */
  const roster = peopleAt(businessId);
  const s = state();

  /* Real bookings first — the ones this browser actually made. */
  const booked = [];
  Object.entries(s.records).forEach(([uid, rows]) => {
    rows.forEach(r => {
      if (r.businessId !== businessId) return;
      if (!String(r.time || '').startsWith(dateISO)) return;
      const c = s.customers[uid];
      booked.push({
        time: String(r.time).slice(11, 16),
        /* The length actually booked. It was on the record and not on the
           diary row, so anything reading the diary — the desk's own calendar
           export among them — had to guess an hour. */
        minutes: Number(r.meta?.minutes) || null,
        service: r.meta?.service || r.subtitle,
        /* The diary's column is a person at every other trade. A restaurant's
           diary is the tables, so the row says which one rather than naming a
           host nobody chose. */
        staff: r.meta?.staff || r.meta?.table_words || roster[0]?.name || '',
        staffId: r.meta?.staff_id || roster[0]?.id || null,
        tables: r.meta?.tables || null,
        guests: r.meta?.guests || null,
        status: r.status || 'confirmed',
        /* The appointment's own id and its hold, so the desk can see the
           money and capture it from the row it is looking at. */
        recordId: r.id,
        auth: r.auth || null,
        /* What was ordered and how the customer said they would pay.
           Chosen at booking — a fact about the booking, not about money
           that has moved, and the screens keep those two apart. Without
           these the desk could see THAT somebody was coming and never what
           for, which is the first thing it opens an appointment to learn. */
        payment: r.meta?.payment || null,
        products: r.meta?.product_lines || [],
        amount: r.amount ?? null,
        serviceAmount: r.meta?.service_amount ?? null,
        note: r.meta?.note || null,
        source: 'avtale.net',
        customer: c
          ? disclosedTo(c, b, true)
          : { name: '(no account yet)', phone: uid },
      });
    });
  });

  /* Plus the demo diary, so an empty browser still shows a working day. The
     two are marked apart on purpose — a demo row must never be mistaken for a
     booking somebody made.

     ── And nothing at all where there is no price list ────────────────────

     This line read `b.services[i % b.services.length]`, which for a business
     with an EMPTY list is `i % 0` — NaN — and `services[NaN]` is undefined.
     Reading `.name` off it threw, React unmounted the tree, and the whole
     business surface went to a white page on its very first tab.

     Every business that signs up through the form starts with no price list,
     so this was every new tenant's first sight of their own console. The
     same guard already existed in `seedHistory` ("a trade with no range gets
     no message rather than an empty one") and was missed here.

     A business with nothing to sell has nothing to draw a sample day from,
     and an invented service name would be worse than an empty diary — so
     the honest answer is no demo rows, and the screen shows a real empty
     day. */
  const range = b.services || [];
  const demo = !range.length ? [] : demoDay(businessId, dateISO).map((x, i) => ({
    time: fromMinutes(x.from),
    end: fromMinutes(x.to),
    service: range[i % range.length].name,
    staff: x.person?.name || '',
    staffId: x.person?.id || null,
    status: 'confirmed',
    source: 'demo',
    customer: null,
  }));

  /* `staffId` is an id in the Chamcore adapter and was a *name* here — the
     two adapters disagreed about what the third argument means, so the same
     call narrowed correctly against one backend and silently returned nothing
     against the other. It is an id in both now, and the row carries both so
     the surface can show the name without a second lookup. */
  const all = [...booked, ...demo]
    .filter(r => !staffId || r.staffId === staffId)
    .sort((x, y) => x.time.localeCompare(y.time));
  return wait(all);
}

/* ── Registering a customer from the business's side ──────────────────────
   Reception has somebody in front of them, or on the telephone, and that
   person is not going to open an app to be written into the book. So the
   business writes them in.

   What that does and does not do:

   **It creates the relation.** From here on this person is this business's
   customer, appears in the CRM, and can be booked in from the diary.

   **It creates no account.** The platform does not now hold a person who
   never came to it — `s.customers` is untouched. What the business typed is
   the business's own note, kept on its own relation, and the CRM shows that
   and nothing else. If the number belongs to somebody who already has an
   Avtale account, the business still sees only what it typed: the alternative
   is a form that turns a phone number into a verified name and address, which
   is the one thing this platform promises it will not do.

   **It sends nothing.** No SMS goes out because a receptionist typed a
   number. The confirmation belongs to a booking, not to a filing.  */
export function registerCustomer(businessId, input) {
  const b = catalogue().find(x => x.id === businessId);
  if (!b) throw new Error('Unknown business.');

  const name = String(input?.name || '').trim();
  if (!name) throw new Error('A customer needs a name.');
  if (name.length > 80) throw new Error('That name is too long.');

  const f = phoneError(input?.phone);
  if (f) throw new Error(f);
  const uid = normalise(input.phone);

  const email = String(input?.email || '').trim();
  if (email && !/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(email))
    throw new Error('That does not look like an email address.');

  const s = state();
  s.relations[uid] = s.relations[uid] || [];
  const had = s.relations[uid].find(r => r.businessId === businessId);
  if (had) throw new Error(`${name} is already one of your ${words(b.industry).customers.toLowerCase()}.`);

  const rel = {
    businessId,
    industry: b.industry,
    since: new Date().toISOString().slice(0, 10),
    number: new Date().getFullYear() + '-' + (1000 + s.relations[uid].length * 7 + 3),
    by: 'business',
    own: { name, phone: uid, email: email || null, note: String(input?.note || '').trim() || null },
  };
  s.relations[uid].push(rel);
  write(s);

  return fetchBusinessCustomers(businessId);
}

/* ── An enquiry ───────────────────────────────────────────────────────────

   Where the work cannot be priced from a list, the way in is a question
   rather than a calendar. The form for it has existed on every advisory
   trade's page since the layouts were built — and it went nowhere. A customer
   who enquired with a law firm saw a confirmation window and then, on their
   own page, no trace of ever having asked. That is the inbox problem this
   platform exists to solve, rebuilt inside the platform.

   So an enquiry is a record, on both sides of it:

     the customer   gets a line on My page saying who they asked, when, and
                    what they wrote — so "did I ever ring that firm?" has an
                    answer
     the firm       gets the person in its book, with the enquiry in the
                    history, because somebody who has handed over their name
                    and number and asked a question IS a client of theirs now

   ── What the firm is told, and why it is not the account ──────────────────

   The fields are the ones the person TYPED, not the ones the platform holds.
   That is the same argument as `registerCustomer` seen from the other end: an
   enquiry is the customer choosing to hand over a name and a number, so the
   firm gets exactly that and not a verified identity behind it. If they go on
   to book and authorise, `by` becomes 'customer' and the account takes over —
   which is the existing upgrade path and needs no exception.

   Nothing is sent. There is no mail server and no SMS gateway in this build,
   and the screen says so rather than implying a message has left. */
export function sendEnquiry(businessId, input) {
  const business = catalogue().find(x => x.id === businessId);
  if (!business) throw new Error('Unknown business.');

  const name = String(input?.name || '').trim();
  if (!name) throw new Error('We need a name to ask for.');
  const f = phoneError(input?.phone);
  if (f) throw new Error(f);
  const about = String(input?.about || '').trim();
  if (!about) throw new Error('Write a line or two about what it is about.');

  const uid = normalise(input.phone);
  const s = state();
  const w = words(business.industry);

  s.relations[uid] = s.relations[uid] || [];
  let rel = s.relations[uid].find(r => r.businessId === businessId);
  if (!rel) {
    rel = {
      businessId,
      industry: business.industry,
      since: new Date().toISOString().slice(0, 10),
      number: new Date().getFullYear() + '-' + (1000 + s.relations[uid].length * 7 + 3),
      by: 'enquiry',
      own: { name, phone: uid },
    };
    s.relations[uid].push(rel);
  } else if (rel.by === 'enquiry') {
    /* A second enquiry from the same person updates what they called
       themselves this time; it does not create a second relation. */
    rel.own = { ...(rel.own || {}), name, phone: uid };
  }

  const record = {
    id: newId('Q'),
    businessId,
    type: 'enquiry',
    title: 'Enquiry',
    subtitle: business.name,
    /* An enquiry happens now. It is not an appointment and must never appear
       in "upcoming" — there is nothing to turn up to until they answer. */
    time: new Date().toISOString().slice(0, 16).replace('T', ' '),
    date: shortDate(new Date().toISOString().slice(0, 10)),
    ts: Date.now(),
    status: 'awaiting reply',
    meta: { about, name, service: w.enquiry },
    body: about,
    /* Nothing left the browser. */
    demo: true,
  };
  s.records[uid] = s.records[uid] || [];
  s.records[uid].unshift(record);

  write(s);
  return wait({ record, relation: rel, business: merged(business) });
}

/* What a business typed about somebody it registered itself. Same shape as
   `disclosedTo` returns, so the CRM does not have to know which kind of row
   it is holding — but assembled from the business's own keystrokes rather
   than from anybody's account. */
function ownFields(rel, uid) {
  const own = rel.own || {};
  const out = { name: own.name || '(not given)', phone: own.phone || uid };
  if (own.email) out.email = own.email;
  return out;
}

export function fetchBusinessCustomers(businessId) {
  const b = catalogue().find(x => x.id === businessId);
  if (!b) return wait([]);
  const s = state();
  seedSalon(s, businessId);
  const out = [];
  Object.entries(s.relations).forEach(([uid, rows]) => {
    const rel = rows.find(r => r.businessId === businessId);
    if (!rel) return;                       /* no relation, no row. Ever. */
    /* Where the row's fields come from depends on who made the relation, and
       this is the sharpest edge on the whole platform.

       A relation the CUSTOMER made — they booked — discloses the platform
       account, filtered by what this trade is allowed to be told.

       A relation the BUSINESS made — reception typed somebody in — discloses
       nothing of the sort. It shows what the business itself typed and not
       one field more. If it read the account instead, a business could learn
       the verified name, date of birth and address behind any phone number
       in Norway by typing it into a form, and that is identity harvesting
       with a CRM around it. The upgrade happens when the person books
       through Avtale themselves: `by` becomes 'customer', and from that
       moment the account is theirs to have disclosed. */
    /* A relation exists, so a row exists. What goes in it depends on what
       is actually known.

       `disclosedTo` answers null when there is no platform account, and for
       a while that dropped the row entirely — which emptied the client list
       of any firm whose first meeting is free. A free appointment holds no
       money, so nothing is authorised, so nobody is identified: the client
       is real and the platform simply does not know their name yet. Hiding
       them was the wrong answer to that; saying so is the right one. */
    /* An enquiry is the third case, and it sits with the business's own note
       rather than with the account: the person typed their name and number
       into a form on the firm's page, so the firm gets what they typed. It
       upgrades the same way — book and authorise, and `by` becomes
       'customer'. */
    const fields = rel.by === 'customer'
      ? (disclosedTo(s.customers[uid], b, true) || { name: '(not identified)', phone: uid })
      : ownFields(rel, uid);
    if (!fields) return;
    /* Everything this business is allowed to know about this person, in one
       row, so the CRM does not have to make a second call per customer and
       so the disclosure rule is applied exactly once, here.

       `history` is this customer's records AT THIS BUSINESS and nowhere
       else. The whole platform rests on that word: a business sees its own
       dealings with a person and has no window into anybody else's. */
    const mine = (s.records[uid] || []).filter(r => r.businessId === businessId);
    const visits = mine.filter(r => r.type === 'visit' || r.status === 'done');
    out.push({
      id: rel.number,
      number: rel.number,
      since: rel.since,
      /* Who put this person in the book. The CRM says so on the card, because
         "we wrote this down ourselves" and "they booked and identified with
         Vipps" are two very different degrees of confidence in a name, and a
         screen that showed them identically would be inviting the business to
         trust its own typing as much as a verified identity. */
      by: rel.by || 'customer',
      /* Whether the platform has actually identified this person. A free
         first meeting authorises nothing, so it identifies nobody — and a
         CRM that showed an unidentified client the same as a Vipps-verified
         one would be inviting the firm to trust a name nobody checked. */
      identified: !!s.customers[uid],
      note: rel.own?.note || null,
      fields,
      /* The searchable text is built from the disclosed fields only. A CRM
         that could find somebody by a field it is not allowed to display
         would be the disclosure rule with a back door in it. */
      haystack: Object.values(fields).join(' ').toLowerCase(),
      visits: visits.length,
      entries: mine.length,
      last: mine.map(r => r.time).filter(Boolean).sort().slice(-1)[0] || null,
      next: mine
        .filter(r => r.type === 'agreement' && r.status !== 'cancelled')
        .map(r => r.time).filter(Boolean).sort()
        .find(t => t >= new Date().toISOString().slice(0, 16).replace('T', ' ')) || null,
      spent: mine
        .filter(r => r.auth?.state === 'captured')
        .reduce((n, r) => n + (r.auth.charged || 0), 0),
      history: mine
        .slice()
        .sort((a, b) => String(b.time || '').localeCompare(String(a.time || '')))
        .map(r => ({
          id: r.id,
          type: r.type,
          title: r.title,
          service: r.meta?.service || null,
          /* At a restaurant there is no host on the booking and the table is
             the fact worth showing — see confirmBooking. */
          staff: r.meta?.staff || r.meta?.table_words || null,
          time: r.time || null,
          date: r.date || null,
          status: r.status || null,
          amount: r.amount || null,
          auth: r.auth ? { state: r.auth.state, amount: r.auth.amount, charged: r.auth.charged } : null,
          products: r.meta?.products || [],
        })),
    });
  });
  return wait(out);
}

/* ── The price list ───────────────────────────────────────────────────────
   The business's own list: what it offers, how long each one takes, what it
   costs, and which of its people do it. Every one of those four is the
   business's to set and nobody else's — a platform that decided how long a
   colour takes would be telling a salon how to run its chair.

   It is one call rather than four, because the four belong together: change
   a duration and the free times move, change who offers it and the person
   picker on the booking screen changes with it. Saving them separately is how
   a list ends up half-changed.

   Validated here, not in the form. The client can lie, and a duration of zero
   or a price of minus five would go straight into somebody's calendar. */
export function saveServices(businessId, list) {
  const b = catalogue().find(x => x.id === businessId);
  if (!b) throw new Error('Unknown business.');
  const roster = peopleAt(businessId).map(p => p.id);

  const clean = (list || []).map((t, i) => {
    const name = String(t.name || '').trim();
    if (!name) throw new Error(`Line ${i + 1} has no name.`);
    if (name.length > 80) throw new Error(`"${name.slice(0, 20)}…" is too long a name.`);

    /* Minutes. A booking is a block of time on somebody's day, so this is the
       one field with no sensible empty value. */
    const minutes = Math.round(Number(t.minutes));
    if (!Number.isFinite(minutes) || minutes < 5)
      throw new Error(`"${name}" needs a length of at least 5 minutes.`);
    if (minutes > 8 * 60)
      throw new Error(`"${name}" is longer than a working day. Split it, or book it as two.`);
    if (minutes % 5)
      throw new Error(`"${name}" has to be a multiple of 5 minutes — the calendar is drawn in blocks.`);

    /* The price MAY be empty. A trade that quotes each job has no number to
       put here, and forcing a zero would print "0 kr" on a public page. */
    let price = null;
    if (t.price !== '' && t.price != null) {
      price = Math.round(Number(t.price));
      if (!Number.isFinite(price) || price < 0)
        throw new Error(`"${name}" has a price that is not a number of kroner.`);
      if (price > 500000) throw new Error(`"${name}" has a price that looks like a mistake.`);
    }

    /* Who offers it. An empty list means everybody — which is the right
       default for a salon where any chair can cut hair, and the thing a law
       firm overrides the moment it has two people who do different work. */
    const staff = (t.staff || []).filter(id => roster.includes(id));

    return {
      name,
      description: String(t.description || '').trim().slice(0, 240),
      minutes,
      unit: minutes + ' min',
      price,
      priceText: price == null
        ? 'By arrangement'
        : price === 0 ? 'Free' : `from ${price.toLocaleString('nb-NO')} kr`,
      staff,
      icon: t.icon || 'star',
    };
  });

  if (!clean.length) throw new Error('A business needs at least one thing on its list.');
  const names = clean.map(t => t.name.toLowerCase());
  const twice = names.find((n, i) => names.indexOf(n) !== i);
  if (twice) throw new Error(`"${twice}" is on the list twice.`);

  const s = state();
  s.businesses = s.businesses || {};
  s.businesses[businessId] = { ...(s.businesses[businessId] || {}), services: clean };
  write(s);
  return wait(merged(b));
}

export function saveBusiness(businessId, patch) {
  const b = catalogue().find(x => x.id === businessId);
  if (!b) throw new Error('Unknown business.');
  const s = state();
  s.businesses = s.businesses || {};
  const next = { ...(s.businesses[businessId] || {}), ...patch };
  if (patch.slug != null) {
    const slug = String(patch.slug).toLowerCase();
    if (!/^[a-z0-9-]{3,64}$/.test(slug)) throw new Error('A slug is 3–64 characters of a–z, 0–9 and hyphen.');
    if (RESERVED.includes(slug)) throw new Error(`"${slug}" is a platform route and cannot be a slug.`);
    if (catalogue().some(x => x.id !== businessId && x.slug === slug))
      throw new Error('Another business already has that address.');
    next.slug = slug;
  }

  /* ── Who the business is ──────────────────────────────────────────────
     The organisation number, the address and the telephone. There was
     nowhere in the whole console to enter any of them, which is why Sales
     kept saying a sales document must carry the seller's number and the page
     kept refusing to go public.

     Checked here rather than trusted from the form, because a number that
     fails mod-11 was never issued by Brønnøysundregistrene and belongs to
     nobody — and this platform's oldest rule is that a business number is
     never invented. Blank is allowed: a tenant who has not got to it yet is
     in a different position from one who typed it wrong, and the screens
     already say which fields are still missing. */
  if (patch.org_no !== undefined) {
    const d = String(patch.org_no || '').replace(/\s/g, '');
    if (d) {
      const bad = orgError(d);
      if (bad) throw new Error(bad);
    }
    next.org_no = d;
  }
  /* Whether "MVA" may be printed after that number. It is a claim, so it
     follows an answer rather than the number looking like one — see
     domain/salesdoc.js. A look-up against the VAT register is what should
     normally set it. */
  if (patch.vat_registered !== undefined) next.vat_registered = !!patch.vat_registered;

  ['street', 'postcode', 'city', 'phone', 'leader'].forEach(k => {
    if (patch[k] === undefined) return;
    next[k] = String(patch[k] ?? '').trim().slice(0, 120);
  });
  if (patch.postcode !== undefined && next.postcode
      && !/^\d{4}$/.test(next.postcode)) {
    throw new Error('A Norwegian postcode is four digits.');
  }

  /* The picture. `image` is an id from the industry's pack; `image_custom` is
     one the business uploaded. Setting either clears the other, because two
     answers to "what is your hero" is how a page ends up showing the one you
     replaced. */
  if (patch.image != null) {
    /* Its own industry's pack, and no other. A salon showing a law firm's
       meeting room is not a bug anyone would report, and it would sit there
       for months. */
    if (patch.image && !pack(b.industry).some(r => r.id === patch.image))
      throw new Error('That picture is not in this industry\'s pack.');
    next.image = patch.image || null;
    next.image_custom = null;
  }
  /* ── Social accounts and campaign posts ───────────────────────────────
     Both are the tenant's own words and go out on their public page, so both
     are cleaned here rather than trusted from the form. A handle is stored as
     a handle, not as a URL somebody pasted with a tracking query on the end,
     and a post is capped at a length that still reads as a card. */
  /* The shelf. Keyed by product id, and only the two fields that are the
     tenant's to set — what they charge and whether they stock it. A price
     below zero, or a product that is not in this trade's range, is refused
     here rather than drawn on a page. */
  if (patch.shop != null) {
    const allowed = new Set(productsFor(b.industry).map(p => p.id));
    const clean = { ...(next.shop || {}) };
    for (const [id, row] of Object.entries(patch.shop || {})) {
      if (!allowed.has(id)) throw new Error('That product is not in your trade\'s range.');
      const entry = {};
      if (row.price != null && row.price !== '') {
        const n = Math.round(Number(row.price));
        if (!Number.isFinite(n) || n < 0) throw new Error('A price is a whole number of kroner, or nothing.');
        if (n > 100000) throw new Error('That price looks like a mistake.');
        entry.price = n;
      }
      entry.stocked = row.stocked !== false;
      clean[id] = entry;
    }
    next.shop = clean;
  }

  if (patch.social != null) {
    const clean = {};
    for (const [channel, row] of Object.entries(patch.social || {})) {
      const handle = String(row?.handle || '').trim()
        .replace(/^https?:\/\//, '')
        .replace(/[?#].*$/, '')
        .replace(/\/+$/, '')
        .slice(0, 120);
      if (!handle) continue;
      clean[channel] = { handle, on: row.on !== false };
    }
    next.social = clean;
  }

  if (patch.posts != null) {
    next.posts = (patch.posts || [])
      .map(p => ({
        title: String(p?.title || '').trim().slice(0, 90),
        text: String(p?.text || '').trim().slice(0, 240),
        tag: String(p?.tag || '').trim().slice(0, 30) || null,
        image: p?.image || null,
      }))
      .filter(p => p.title || p.text)
      .slice(0, 3);
  }

  if (patch.image_custom != null) {
    const url = String(patch.image_custom || '');
    if (url) {
      if (!/^data:image\/(png|jpeg|webp|svg\+xml);/.test(url))
        throw new Error('Upload a PNG, JPEG or WebP.');
      /* A browser gives a page about 5 MB of localStorage in total, and this
         is one field of one business. In production the file goes to storage
         and this field holds its URL — the cap goes with the storage. */
      if (url.length > 700000) throw new Error('That file is too large. Under 500 kB, please.');
    }
    next.image_custom = url || null;
    if (url) next.image = null;
  }

  s.businesses[businessId] = next;
  write(s);
  return wait({ ...b, ...next });
}

export function fetchPlatform() {
  const s = state();
  const edits = s.businesses || {};
  return wait({
    industries: INDUSTRIES.map(i => ({
      ...i,
      businesses: catalogue().filter(b => b.industry === i.code).length,
    })),
    businesses: BUSINESSES.map(b => {
      const m = { ...b, ...(edits[b.id] || {}) };
      return {
        id: m.id, name: m.name, slug: m.slug, industry: m.industry, city: m.city,
        visible: m.visible !== false && complete(m),
        missing: [
          !m.street && 'address',
          !m.opening_hours && 'opening hours',
          !m.services?.length && 'at least one service',
          !(STAFF[m.id] || []).length && 'at least one member of staff',
        ].filter(Boolean),
      };
    }),
  });
}

export function reset() {
  write(empty());
  return wait(true);
}

/* ── The payment rails ────────────────────────────────────────────────────
   Four rails, per business, because the money is the business's and the
   credentials are the business's — see domain/payments.js on why that is the
   right place for them rather than a platform account with a payout run.

   The one thing this adapter does that a naive port of the Chamcore screen
   would not: it refuses to write the secrets down.

   A client secret, a Stripe API key and a Vipps subscription key are bearer
   credentials. Whoever holds one can take money in that merchant's name.
   localStorage is readable by every script on the page and survives in a
   browser profile that other people use, so a demo that kept them there
   would be a demo that leaked real merchant credentials — and these are the
   kind somebody pastes in from the real portal "just to see if it saves".

   So `stripSecrets` runs on the way in, the values are dropped, and what is
   stored is the list of which secrets have been set. The screen can then say
   "a secret is stored" truthfully and show what is still missing, and there
   is nothing here worth stealing. When there is a backend, the secret goes
   from the form to the vault without passing through this file either. */
export function fetchPayments(businessId) {
  const b = merged(catalogue().find(x => x.id === businessId));
  if (!b) return wait(null);
  const saved = (b.payments || {});
  const subs = (b.subscriptions || {});
  return wait(Object.fromEntries(RAILS.map(r => {
    const mine = saved[r.id] || {};
    const sub = subs[r.id] || { state: 'none' };
    return [r.id, {
      /* Two different things, and the screen needs both. `enabled` is what
         the tenant asked for; `sub` is whether we are being paid for it.
         A rail is only actually taking money when both are true, which is
         what `live` says — and it is computed here rather than in the
         component so that no surface can accidentally treat a configured
         but unpaid rail as a working one. */
      enabled: mine.enabled === true,
      sub,
      live: mine.enabled === true && subUsable(sub),
      mode: MODES.includes(mine.mode) ? mine.mode : 'test',
      values: mine.values || {},
      /* Which secrets exist. Never what they are. */
      set: Array.isArray(mine.set) ? mine.set : [],
      choices: mine.choices || {},
    }];
  })));
}

export function savePayments(businessId, railId, patch) {
  const b = catalogue().find(x => x.id === businessId);
  if (!b) throw new Error('Unknown business.');
  if (!railDef(railId)) throw new Error('Unknown payment rail.');

  const mode = MODES.includes(patch?.mode) ? patch.mode : 'test';

  /* The same validation the backend will have to do. A form can be bypassed;
     a rail configured with a live key in test mode cannot be allowed through
     just because the input that would have caught it was not rendered. */
  const bad = railProblems(railId, patch?.values || {}, mode);
  if (bad.length) throw new Error(bad[0][1]);

  /* Switching a rail on is a commercial act, not a toggle. The subscription
     has to be running, and the check is here rather than only in the form
     because a form can be bypassed and this one decides whether we are paid. */
  const subs = (state().businesses?.[businessId]?.subscriptions) || {};
  if (patch?.enabled === true && !subUsable(subs[railId] || { state: 'none' }))
    throw new Error(
      `${railDef(railId).name} is not subscribed, so it cannot be switched on. ` +
      'Configure it as much as you like — it starts taking money when the ' +
      'monthly subscription is running.',
    );

  const { keep, set } = stripSecrets(railId, patch?.values || {});

  const s = state();
  s.businesses = s.businesses || {};
  const row = { ...(s.businesses[businessId] || {}) };
  const all = { ...(row.payments || {}) };
  const had = all[railId] || {};

  all[railId] = {
    enabled: patch?.enabled === true,
    mode,
    values: keep,
    /* A secret already set stays set unless the tenant clears the field
       deliberately — `patch.clear` names the ones to forget. Retyping a
       secret you cannot see, every time you change a due date, is how a
       tenant ends up pasting credentials into a form far more often than
       they need to. */
    set: Array.from(new Set([
      ...(had.set || []).filter(id => !(patch?.clear || []).includes(id)),
      ...set,
    ])),
    choices: patch?.choices || had.choices || {},
  };

  row.payments = all;
  s.businesses[businessId] = row;
  write(s);
  return wait(merged(catalogue().find(x => x.id === businessId)));
}

/* ── An evening at one table ──────────────────────────────────────────────

   The guest scans the card on the table and orders from there. Sessions are
   keyed by the table, because the code on the table is the only thing that
   identifies one — no guest and no screen ever types a table number.

   Everything here is the demo stand-in: the holds reserve nothing, because
   there are no merchant keys, and the kitchen screen is a list in a browser
   rather than a screen in a kitchen. The states and the rules are real. */
function sessionOf(s, businessId, table) {
  s.tables = s.tables || {};
  const k = sessionKey(businessId, table);
  if (!s.tables[k]) s.tables[k] = emptySession(businessId, table);
  return s.tables[k];
}

const dressSession = sess => ({
  ...sess,
  lines: billLines(sess),
  total: totalOf(sess),
  waiting: needingWaiter(sess),
  open: isOpen(sess),
});

/** What can be ordered here, and the note above it. */
export function fetchMenu(businessId) {
  if (!hasMenu(businessId)) return wait(null);
  return wait({ intro: menuIntro(businessId), groups: menuGroups(businessId) });
}

/**
 * The table a code points at.
 *
 * Refuses a number the place does not have rather than opening a session on
 * it — a code that has been mistyped or that belongs to another restaurant
 * must not quietly create table 99.
 */
export function fetchTable(businessId, table) {
  const b = catalogue().find(x => x.id === businessId);
  const n = Number(table);
  const spot = tablesAt(businessId).find(t => t.n === n);
  if (!b || !spot) return wait(null);
  const s = state();
  const sess = sessionOf(s, businessId, n);
  return wait({
    business: merged(b),
    table: spot,
    session: dressSession(sess),
  });
}

/**
 * Check in.
 *
 * `age` is the answer a verification gave — `{ over18, over20 }` — and never
 * a date of birth. The platform keeps the answer, for as long as the session
 * lasts, and nothing else.
 */
export function checkInAtTable(businessId, table, { name, age = null, by = null, mode, phone } = {}) {
  const spot = tablesAt(businessId).find(t => t.n === Number(table));
  if (!spot) throw new Error('There is no such table here.');
  const s = state();
  const sess = sessionOf(s, businessId, Number(table));
  if (sess.settled) {
    /* The table was paid and cleared. A new party at the same table is a new
       evening, not a reopening of somebody else's bill. */
    s.tables[sessionKey(businessId, Number(table))] = emptySession(businessId, Number(table));
  }
  let next = s.tables[sessionKey(businessId, Number(table))];
  /* The first guest sets the bill. Afterwards it is the table's. */
  if (mode && mayChooseMode(next)) next = { ...next, mode };
  next = joinTable(next, { name, age, by, phone: normalise(phone || '') || null });
  s.tables[sessionKey(businessId, Number(table))] = next;
  write(s);
  return wait({ session: dressSession(next), guestId: next.guests.at(-1).id });
}

/** Order. Throws with the reason rather than dropping a line. */
export function orderAtTable(businessId, table, guestId, lines = []) {
  const s = state();
  const sess = sessionOf(s, businessId, Number(table));
  const priced = lines.map(l => {
    const row = menuLine(businessId, l.id);
    if (!row) throw new Error('That is not on the menu here.');
    /* The price comes from the menu, never from the basket the browser sent.
       A client that could name its own price is a client that will. */
    return { ...row, qty: Math.max(1, Math.round(Number(l.qty) || 1)) };
  });
  const next = placeOrder(sess, guestId, priced);
  s.tables[sessionKey(businessId, Number(table))] = next;
  write(s);
  return wait(dressSession(next));
}

/** The waiter's approval, and the kitchen's progress — the same line. */
export function moveOrder(businessId, table, orderId, to) {
  const s = state();
  const sess = sessionOf(s, businessId, Number(table));
  const next = advanceOrder(sess, orderId, to);
  s.tables[sessionKey(businessId, Number(table))] = next;
  write(s);
  return wait(dressSession(next));
}

/** What the payer would owe, and what everybody else would be asked for. */
export function previewSplit(businessId, table, payerId, opts) {
  const s = state();
  return wait(splitBill(sessionOf(s, businessId, Number(table)), payerId, opts));
}

/**
 * Settle.
 *
 * The holds are captured and the table closes. The requests to the other
 * guests are recorded as requests — nothing is taken from anybody, because a
 * request asks and does not take, and an unpaid one stays on the payer.
 */
export function settleTable(businessId, table, payerId, opts = {}) {
  const s = state();
  const sess = sessionOf(s, businessId, Number(table));
  if (!isOpen(sess)) throw new Error('This table is already settled.');
  const share = splitBill(sess, payerId, opts);
  const next = settleSession(sess);
  s.tables[sessionKey(businessId, Number(table))] = next;

  /* ── The receipts ──────────────────────────────────────────────────────
     Not only for whoever tapped the card. Four people eat together, one
     pays, and the other three leave with nothing to show for the share they
     were asked for — which is how it works today and is the wrong way round.
     So everybody on the split gets one: the payer's shows the whole table,
     everybody else's shows their share and what it is a share of.

     It reaches whoever the platform has a number for. Nobody is asked to
     type an email address at the end of a meal, and there is no mail server
     to send one with — see domain/receipt.js. */
  const payer = next.guests.find(g => g.id === payerId);
  const b = catalogue().find(x => x.id === businessId);
  const lines = billLines(next);
  const now = new Date();
  const made = [];

  share.parts.forEach(part => {
    const isPayer = part.guest.id === payerId;
    /* The payer's own number comes from the screen they settled on; anybody
       else's is what they checked in with. No number, no receipt — rather
       than a receipt filed against a stranger. */
    const uid = normalise((isPayer ? opts.phone : part.guest.phone) || '');
    if (!uid) return;
    const paper = buildReceipt({
      business: b,
      table: Number(table),
      /* The payer sees every line on the table. Somebody asked for a share
         sees what they themselves ordered when the split went by spend, and
         the table's lines when it was divided evenly — because an even split
         is a share of the whole and showing only their own dishes would not
         add up to it. */
      lines: (isPayer || opts.mode === 'even') ? lines : billLines(next, part.guest.id),
      total: share.total,
      share: part.amount,
      mode: opts.mode || 'spent',
      name: part.guest.name,
      payer: isPayer,
      at: now.toISOString(),
    });
    s.records[uid] = s.records[uid] || [];
    s.records[uid].unshift({
      id: newId('T'),
      businessId,
      type: 'receipt',
      title: isPayer ? 'Table bill' : 'Your share of the table',
      subtitle: `${b?.name || ''} · ${receiptSummary(paper)}`,
      time: now.toISOString().slice(0, 16).replace('T', ' '),
      date: shortDate(now.toISOString().slice(0, 10)),
      ts: Date.now(),
      amount: part.amount,
      status: isPayer ? 'paid' : 'asked',
      meta: {
        service: 'Table bill',
        tables: [Number(table)],
        guests: next.guests.length,
        receipt: paper,
        requests: isPayer
          ? share.parts.filter(p => p.guest.id !== payerId)
              .map(p => ({ name: p.guest.name, amount: p.amount, state: 'asked' }))
          : undefined,
      },
      demo: true,
    });
    made.push({ name: part.guest.name, payer: isPayer });
  });

  write(s);
  /* `receipts` is who actually got one, so the screen can say "two of you
     have it on your page" rather than implying everybody did. */
  return wait({ session: dressSession(next), share, payer, receipts: made });
}

/** Every table with something on it, for the desk. */
export function fetchTables(businessId) {
  const s = state();
  s.tables = s.tables || {};
  const live = Object.values(s.tables)
    .filter(t => t.businessId === businessId && (t.guests.length || t.orders.length))
    .map(dressSession)
    .sort((a, b) => a.table - b.table);
  return wait({ tables: live, plan: tablesAt(businessId) });
}

/* ── A business signs up ──────────────────────────────────────────────────

   Chamcore's own two steps, in Chamcore's own order: `Payments::createClient`
   makes the client, and `Payments::subscription` takes it to Stripe. They are
   separate on purpose there and separate here, and the reason is worth
   keeping: a business exists on the platform whether or not it ever
   subscribes. Signing up costs nothing, the page can be built, and the
   subscription is what lets a RAIL take money — see domain/billing.js on the
   two flows of money that both say "Stripe".

   What is created is a row this browser holds, alongside the seeded
   catalogue — see `catalogue()` at the top of this file. Its page is not
   public yet and `complete()` is the gate that says so; the console tells the
   tenant what is still needed rather than leaving them to guess why nothing
   is at their address. */
export function createTenant(input) {
  const form = cleanSignup(input);
  const opts = {
    taken: catalogue().map(b => b.slug).filter(Boolean),
    reserved: RESERVED,
    industries: INDUSTRIES.map(i => i.code),
  };
  const bad = signupProblems(form, opts);
  const first = Object.values(bad)[0];
  if (first) throw new Error(first);

  const s = state();
  s.tenants = s.tenants || {};

  /* One account per email, so pressing the button twice does not make two
     businesses. The second press is an error and not a duplicate. */
  if (Object.values(s.tenants).some(t => t.email === form.email))
    throw new Error('There is already an account on that email address.');

  const id = 'biz-' + form.slug.replace(/[^a-z0-9]/g, '').slice(0, 24)
           + '-' + Math.random().toString(36).slice(2, 6);
  const ind = industry(form.industry);

  const row = {
    id,
    slug: form.slug,
    name: form.name,
    /* The wordmark is the name until the tenant sets one; `Logo` splits it.
       Nothing here invents a tagline or a headline either — an empty page
       the tenant fills in beats a page of plausible-sounding filler they
       then have to find and delete. */
    logo: [form.name],
    mark: form.name.trim().charAt(0).toUpperCase(),
    industry: form.industry,
    email: form.email,
    /* The number they signed up on. It is the account's number, not the one
       printed on the page — that one is a field of its own, because the
       person who signs up is not always the one who answers the telephone. */
    account_phone: form.phone,
    phone: '',
    street: '',
    city: '',
    /* **Never invented.** Not here, not anywhere. It is asked for in the
       console, checked for its mod-11 digit, and the page stays unpublished
       until there is one — see domain/signup.js. */
    org_no: '',
    opening_hours: '',
    tagline: '',
    t1: form.name,
    t2: '',
    services: [],
    rating: null,
    created: new Date().toISOString(),
    /* Made in this browser, like everything else here. */
    demo: true,
    /* What the tenant will be asked for, in the order the console asks. */
    industry_name: ind?.name || form.industry,
  };

  s.tenants[id] = row;
  write(s);
  return wait({ business: merged(row), missing: signupMissing({ ...row, staff: [] }) });
}

/** What is still in the way of this page being public. */
export function tenantStatus(businessId) {
  const b = merged(catalogue().find(x => x.id === businessId));
  if (!b) return wait(null);
  const staff = peopleAt(businessId);
  const left = signupMissing({ ...b, staff });
  return wait({
    missing: left,
    publishable: left.length === 0,
    live: left.length === 0 && b.visible !== false,
  });
}

/**
 * Take the new tenant to Stripe.
 *
 * `Payments::subscription` in Chamcore: a package plus addons, which become
 * ONE Stripe subscription with one item each. No prices are sent — Chamcore's
 * own StripeService says in a comment beside its addon table that prices must
 * come from the database and not from the frontend, and it is right.
 *
 * Nothing is charged here. There are no Stripe keys on this platform, so no
 * Checkout Session is created and the link goes nowhere; the state machine is
 * real and the money is not. `completeSubscription` below stands in for the
 * webhook, because in production the browser may not confirm its own payment.
 */
export function subscribeTenant(businessId, { planId, rails = [] } = {}) {
  const b = catalogue().find(x => x.id === businessId);
  if (!b) throw new Error('Unknown business.');
  if (!planOf(planId)) throw new Error('Pick a plan.');
  const unknown = rails.find(r => !railDef(r));
  if (unknown) throw new Error('Unknown payment rail.');

  const s = state();
  const subs = subsOf(s, businessId);
  const started = new Date().toISOString();

  /* One item per rail, plus the plan. The console's own per-rail screen adds
     and removes these same items later, which is what Chamcore's
     `createOrUpdateClientSubscription` does — it never opens a second
     subscription. */
  rails.forEach(railId => {
    subs[railId] = { ...(subs[railId] || {}), state: 'onboarding', started, demo: true };
  });
  s.businesses[businessId].plan = { id: planId, state: 'onboarding', started, demo: true };
  write(s);

  return wait({
    url: checkoutLink(businessId, planId),
    order: billOrder({ planId, rails }),
    /* The shape Stripe answers with, so the screen that reads it does not
       change the day there is a real session. */
    session: { id: null, demo: true },
  });
}

/* ── The subscription to us ───────────────────────────────────────────────
   The other flow of money, and the one this platform is on the receiving end
   of. Per rail, because that is how it is priced: a business that only sends
   invoices pays for invoices.

   Everything here is the demo stand-in for Stripe. In production `start`
   creates a Checkout Session on AVTALE.NET'S account and `complete` is not a
   function anybody calls — it is a webhook from Stripe saying the first
   payment cleared. Writing it as a function the screen can call is the
   honest shape of a demo; writing it as though the browser could confirm its
   own payment would not be, so the screen says which it is. */
function subsOf(s, businessId) {
  s.businesses = s.businesses || {};
  s.businesses[businessId] = s.businesses[businessId] || {};
  s.businesses[businessId].subscriptions = s.businesses[businessId].subscriptions || {};
  return s.businesses[businessId].subscriptions;
}

export function startSubscription(businessId, railId) {
  if (!railDef(railId)) throw new Error('Unknown payment rail.');
  const s = state();
  const subs = subsOf(s, businessId);
  subs[railId] = {
    state: 'onboarding',
    started: new Date().toISOString(),
    /* No Stripe keys on this platform either, so no session is created and
       the link goes nowhere. */
    demo: true,
  };
  write(s);
  return wait({ url: checkoutLink(businessId, railId), sub: subs[railId] });
}

/** Stripe's webhook, standing in for itself. */
export function completeSubscription(businessId, railId) {
  const s = state();
  const subs = subsOf(s, businessId);
  const had = subs[railId] || {};
  subs[railId] = {
    ...had,
    state: 'active',
    since: new Date().toISOString().slice(0, 10),
    demo: true,
  };
  write(s);
  return wait(merged(catalogue().find(x => x.id === businessId)));
}

/**
 * The monthly payment failed, or the tenant ended it.
 *
 * Both switch the rail off the same day — that is the rule that was chosen,
 * and it is the strict one. What neither does is touch the authorisations
 * already held: money held against a customer's card belongs to that
 * customer until it is captured or released, and walking away from a hold
 * because the tenant's own bill bounced would be holding a stranger's money
 * over a dispute they are not part of. So `payments[railId].enabled` goes
 * false and the records are left exactly as they are.
 */
function stopRail(businessId, railId, nextState) {
  const s = state();
  const subs = subsOf(s, businessId);
  subs[railId] = { ...(subs[railId] || {}), state: nextState, ended: new Date().toISOString() };

  const row = s.businesses[businessId];
  row.payments = row.payments || {};
  if (row.payments[railId]) row.payments[railId] = { ...row.payments[railId], enabled: false };

  write(s);
  return wait(merged(catalogue().find(x => x.id === businessId)));
}

export const cancelSubscription = (businessId, railId) =>
  stopRail(businessId, railId, 'cancelled');

/** What Stripe tells us when a card is declined. Here so the consequence can
    be demonstrated and tested rather than described. */
export const failSubscription = (businessId, railId) =>
  stopRail(businessId, railId, 'past_due');

/** Where a card is updated and a subscription ended — Stripe's own portal. */
export const billingPortal = businessId => wait({ url: portalLink(businessId) });

/* ── The rota ─────────────────────────────────────────────────────────────
   Drafted by management or reception, checked against arbeidsmiljøloven, and
   then PUBLISHED — which is the word that matters. A rota sitting in a
   drawer commits nobody; the moment it is published it is what the people
   named on it have been told, and it appears on their own screens.

   So a draft and a published rota are two different things and both are
   kept. A manager who changes a published rota has changed somebody's
   plans, and the screen can only say so if it still knows what those plans
   were. `published` is what the staff surface reads; `draft` is what the
   business edits. */
function rotaOf(s, businessId) {
  s.businesses = s.businesses || {};
  s.businesses[businessId] = s.businesses[businessId] || {};
  s.businesses[businessId].rota = s.businesses[businessId].rota
    || { draft: [], published: [], publishedAt: null, agreed: {} };
  return s.businesses[businessId].rota;
}

/**
 * A sample rota, seeded once — as a DRAFT, and never as a publication.
 *
 * Without one the staff surface is a correct, working, completely empty
 * screen, and an empty screen teaches nobody anything. So a fortnight behind
 * and four weeks ahead are generated from the business's own opening hours
 * by the same `generate()` the Rota tab uses.
 *
 * ── And it is not published ──────────────────────────────────────────────
 *
 * This function used to write `published` as well, and that was wrong in a
 * way worth spelling out. On this platform PUBLISHING IS THE WORD THAT
 * CARRIES THE WEIGHT: up to that moment a rota commits nobody, and after it
 * the people named on it have been told. A platform that published one on a
 * tenant's behalf would have committed named people to be somewhere on a
 * Saturday that nobody at that business had decided.
 *
 * The existing check `the rota is never published to begin with` went red
 * the moment I did it, which is exactly what it was written for.
 *
 * So the draft is seeded and the staff surface is given the DRAFT, plainly
 * labelled as the platform's sample and not as anything their employer has
 * decided. Four weeks ahead rather than two, because two is the floor
 * arbeidsmiljøloven § 10-3 sets and a demonstration sitting exactly on the
 * legal minimum teaches the wrong lesson about what good looks like.
 */
function seedRota(s, businessId) {
  const r = rotaOf(s, businessId);
  if (r.seeded || (r.published || []).length) return r;
  r.seeded = true;

  const b = catalogue().find(x => x.id === businessId);
  const people = peopleAt(businessId);
  if (!b || !people.length) return r;

  const start = new Date();
  start.setDate(start.getDate() - 14);
  const dates = Array.from({ length: 14 + 28 }, (_, i) => {
    const d = new Date(start);
    d.setDate(start.getDate() + i);
    return d.toISOString().slice(0, 10);
  });

  const made = rotaGenerate({
    dates,
    people,
    hoursLine: (merged(b) || {}).opening_hours,
    cover: people.length > 2 ? 2 : 1,
  }).map((x, i) => ({ ...x, id: 'SD' + (i + 1), demo: true }));

  r.draft = made;
  write(s);
  return r;
}

export function fetchRota(businessId) {
  const s = state();
  seedRota(s, businessId);
  const r = rotaOf(s, businessId);
  return wait({
    draft: r.draft || [],
    published: r.published || [],
    publishedAt: r.publishedAt || null,
    agreed: r.agreed || {},
    people: peopleAt(businessId),
    hours: (merged(catalogue().find(x => x.id === businessId)) || {}).opening_hours,
  });
}

export function saveRota(businessId, shifts, agreed) {
  const s = state();
  const r = rotaOf(s, businessId);
  r.draft = (shifts || []).map((x, i) => ({
    id: x.id || 'S' + (i + 1),
    staffId: x.staffId,
    date: x.date,
    from: x.from,
    to: x.to,
    breakMin: Number(x.breakMin) || 0,
    note: x.note || null,
  }));
  if (agreed) r.agreed = agreed;
  write(s);
  return fetchRota(businessId);
}

/**
 * Publishing.
 *
 * Two things happen and both matter. The draft becomes the published rota,
 * and every person on it is told — in the same place they get everything
 * else, so a shift change is not an email somebody missed.
 *
 * What is NOT done here: publishing does not check the law and refuse. The
 * checker reports; a manager with a tariffavtale, a written agreement or
 * Arbeidstilsynet's consent may be entirely within their rights on a rota
 * this module would flag, and software that blocked them would be software
 * they stopped using. The findings are attached to the publication instead,
 * so what was known at the time is on the record.
 */
export function publishRota(businessId, findings = []) {
  const s = state();
  const r = rotaOf(s, businessId);
  const before = r.published || [];
  const now = new Date().toISOString();

  r.published = (r.draft || []).map(x => ({ ...x }));
  r.publishedAt = now;
  r.findings = findings;

  /* What changed for each person, so the message says something useful
     rather than "the rota has been updated". */
  const byPerson = {};
  r.published.forEach(x => { (byPerson[x.staffId] ||= { added: [], moved: [] }).added.push(x); });
  before.forEach(x => {
    const still = r.published.find(y => y.id === x.id);
    if (!still) (byPerson[x.staffId] ||= { added: [], moved: [] }).moved.push(x);
  });

  s.staffInbox = s.staffInbox || {};
  Object.entries(byPerson).forEach(([staffId, what]) => {
    const mine = r.published.filter(x => x.staffId === staffId);
    const hours = mine.reduce((n, x) => {
      const a = toMinutes(x.from); const b = toMinutes(x.to);
      return n + ((b > a ? b - a : b + 1440 - a) - (x.breakMin || 0)) / 60;
    }, 0);
    s.staffInbox[staffId] = s.staffInbox[staffId] || [];
    s.staffInbox[staffId].unshift({
      id: newId('R'),
      businessId,
      at: now,
      kind: 'rota',
      title: 'Your shifts have been published',
      body: mine.length
        ? `${mine.length} shift${mine.length === 1 ? '' : 's'}, `
          + `${hours.toFixed(1)} hours in total. First one ${mine
              .map(x => x.date).sort()[0]}.`
        : 'You are not on this rota.',
      shifts: mine,
      /* Nothing is sent anywhere: no SMS gateway, no mail server. The
         message is composed and stored, and the staff screen shows it. */
      demo: true,
    });
  });

  write(s);
  return fetchRota(businessId);
}

/* ── One person's working life, over a range ──────────────────────────────
   The three things that decide whether somebody is free on the fourteenth,
   answered together: the shifts they are rostered for, the appointments
   booked with them, and the days they are away.

   ── Why one call and not thirty ──────────────────────────────────────────

   `fetchDiary` answers one day. A month view built on it makes thirty
   round-trips, each with its own artificial delay, and paints in thirty
   steps — which on a telephone reads as a broken screen rather than a slow
   one. So the range is the argument.

   ── Why the three arrive together ────────────────────────────────────────

   Because the question is about all three at once. A shift with a booking
   in it and a shift with nothing in it look the same on any screen that
   fetched only the rota; a holiday that overlaps a published shift is the
   thing somebody most needs to see, and it is invisible unless both are in
   the same answer. Two calls and a merge in a component is the same data
   with one more place for them to disagree about a date. */
export function fetchMyWork(businessId, staffId, fromISO, toISO) {
  const b = catalogue().find(x => x.id === businessId);
  if (!b) return wait({ shifts: [], appointments: [], absences: [] });
  const s = state();
  seedRota(s, businessId);
  seedLeave(s, businessId);
  const inRange = d => d && d >= fromISO && d <= toISO;

  /* Published first. Where a business has published nothing, the seeded
     SAMPLE draft is shown instead and flagged as such — a working screen
     that says what it is beats an empty one, and it must never be mistaken
     for shifts somebody has actually been given. */
  const rota = s.businesses?.[businessId]?.rota || {};
  const live = rota.published || [];
  const sample = !live.length;
  const source = sample ? (rota.draft || []) : live;

  const shifts = source
    .filter(x => x.staffId === staffId && inRange(x.date))
    .map(x => ({ ...x, businessId, demo: sample || !!x.demo }));

  /* Real bookings only. The sample day the diary draws is generated per
     date from a string hash — useful for showing a working day, useless and
     misleading in somebody's own month, where they would read it as work
     they have been given. */
  const appointments = [];
  Object.entries(s.records).forEach(([uid, rows]) => {
    rows.forEach(r => {
      if (r.businessId !== businessId) return;
      if (r.demo) return;
      const day = String(r.time || '').slice(0, 10);
      if (!inRange(day)) return;
      if (!['agreement', 'visit', 'quote'].includes(r.type)) return;
      const mine = (r.meta?.staff_id || null) === staffId;
      /* A trade where the firm allocates books no person, so nothing is
         anybody's — see `picksPerson`. Those rows are handed over with
         `mine: false` and the screen shows them as the business's work
         rather than silently dealing them out to whoever is looking. */
      if (r.meta?.staff_id && !mine) return;
      const c = s.customers[uid];
      appointments.push({
        id: r.id,
        date: day,
        time: String(r.time).slice(11, 16),
        minutes: Number(r.meta?.minutes) || null,
        service: r.meta?.service || r.subtitle || r.title,
        status: r.status || 'confirmed',
        mine,
        amount: r.amount ?? r.auth?.amount ?? null,
        /* The same gate as everywhere else. A member of staff sees exactly
           what the business may see about a customer and not one field
           more. */
        customer: c ? disclosedTo(c, b, true) : { name: '(no account yet)', phone: uid },
      });
    });
  });

  const absences = ((s.businesses?.[businessId]?.leave?.rows) || [])
    .filter(r => r.staffId === staffId
      && r.state !== 'withdrawn' && r.state !== 'refused'
      && r.from <= toISO && r.to >= fromISO);

  /* How far the published rota runs for this person — from the WHOLE rota,
     not from the range that was asked for. Computing it from the visible
     week said "the rota runs out on Sunday" every single week, which is both
     alarming and false. */
  const plannedTo = live
    .filter(x => x.staffId === staffId).map(x => x.date).sort().pop() || null;

  return wait({
    from: fromISO, to: toISO,
    /* Null where nothing is published, whatever the sample shows. The
       two-week check reads this, and a sample rota must not answer it — a
       business that had published nothing would otherwise be told its plan
       runs four weeks ahead. */
    plannedTo,
    sample,
    shifts: shifts.sort((x, y) => (x.date + x.from).localeCompare(y.date + y.from)),
    appointments: appointments.sort((x, y) => (x.date + x.time).localeCompare(y.date + y.time)),
    absences,
    hours: (merged(b) || {}).opening_hours,
  });
}

/** One person's published shifts and messages — what the staff surface reads.
    It never sees the draft: a shift somebody is thinking about is not a shift
    anybody has been told about. */
export function fetchMyShifts(staffId) {
  const s = state();
  /* Seed wherever this person is on a roster, so the staff surface is not
     empty on a browser that has never opened the business surface. */
  catalogue().forEach(b => {
    if (peopleAt(b.id).some(p => p.id === staffId)) seedRota(s, b.id);
  });
  const out = [];
  Object.entries(s.businesses || {}).forEach(([businessId, row]) => {
    /* Published only, here. This is the older call and the one whose whole
       promise is "what you have been told" — the sample belongs on the plan
       screen, which says what it is. */
    (row.rota?.published || [])
      .filter(x => x.staffId === staffId)
      .forEach(x => out.push({ ...x, businessId }));
  });
  return wait({
    shifts: out.sort((a, b) => (a.date + a.from).localeCompare(b.date + b.from)),
    messages: (s.staffInbox || {})[staffId] || [],
  });
}

/* ── Absence ──────────────────────────────────────────────────────────────
   Holiday, egenmelding, a sykmelding and a day at home with an ill child,
   in one store, because they land on the same rota and a screen that had to
   read four stores to draw one month would draw it wrong.

   ── The line this code exists to hold ─────────────────────────────────────

   An egenmelding is a NOTIFICATION and a holiday is a REQUEST, and they go
   through different functions here on purpose. `reportAbsence` writes a row
   that is already final; `askForHoliday` writes one waiting for an answer
   and `decideHoliday` answers it. There is deliberately no way to call
   `decideHoliday` on a notification — an employer who could decline an
   egenmelding would be deciding whether somebody is ill, and the law does
   not give them that. See domain/leave.js.

   ── The employment record ─────────────────────────────────────────────────

   Birth year, start date, how many children, whether they are alone with
   the care. This is the employer's own record and it stays on the business,
   never on the public person record — a date of birth has no business on a
   page a customer can open.

   What is NOT recorded: what anybody is ill with. A sykmelding is a fact
   that one exists and for how long; the diagnosis is between the employee
   and their doctor and is not the employer's to hold, let alone this
   platform's. The same for a child: `chronic` says NAV has made a decision,
   and nothing anywhere says what about. */

function leaveOf(s, businessId) {
  s.businesses = s.businesses || {};
  s.businesses[businessId] = s.businesses[businessId] || {};
  const b = s.businesses[businessId];
  b.leave = b.leave || { rows: [], staff: {}, settings: {} };
  b.leave.rows = b.leave.rows || [];
  b.leave.staff = b.leave.staff || {};
  b.leave.settings = b.leave.settings || {};
  return b.leave;
}

/* Somebody with nothing recorded is not somebody with no rights. The
   defaults are the ones that ask for the least and claim the least: employed
   long enough to have the statutory egenmelding, the statutory holiday and
   no children recorded — and every screen says which of these is a blank
   rather than a fact. */
const EMPLOYMENT = {
  employedSince: null,
  born: null,
  children: 0,
  soleCarer: false,
  chronic: false,
  rosteredPerWeek: 5,
};

const employmentOf = (leave, staffId) => ({ ...EMPLOYMENT, ...(leave.staff[staffId] || {}) });

/**
 * The demonstration absences.
 *
 * Seeded once per business so the month, the balances and the statistics
 * show something on a browser that has never been used. Every row carries
 * `demo: true` and every screen that counts them says how many of the
 * figures came from here — the alternative is a business reading its own
 * sickness rate off the platform's sample data and believing it.
 */
function seedLeave(s, businessId) {
  const leave = leaveOf(s, businessId);
  if (leave.seeded) return leave;
  leave.seeded = true;

  const roster = peopleAt(businessId);
  if (!roster.length) return leave;

  const back = n => {
    const d = new Date();
    d.setDate(d.getDate() - n);
    return d.toISOString().slice(0, 10);
  };
  const on = (n, len) => ({ from: back(n), to: back(n - (len - 1)) });
  const soon = (n, len) => ({ from: back(-n), to: back(-(n + len - 1)) });

  /* Deliberately ordinary: two short self-reported spells, one certified
     absence, one day with an ill child and a week of holiday already taken.
     Nothing dramatic — a screen tuned on an extreme case reads badly on the
     normal one, which is the one every business will have. */
  const plan = [
    { who: 0, kind: 'self', ...on(54, 2) },
    { who: 0, kind: 'child', ...on(22, 1) },
    { who: 1, kind: 'self', ...on(88, 1) },
    { who: 1, kind: 'sick', ...on(40, 5) },
    { who: 2, kind: 'holiday', ...on(120, 6), state: 'agreed' },
    /* One in the last fortnight, so the month somebody opens the screen on
       has something on it rather than reading as a broken grid. */
    { who: 1, kind: 'child', ...on(6, 1) },
    /* And one waiting for an answer, because the queue is the only part of
       this screen that is a decision, and an empty queue demonstrates
       nothing about how the decision is made. Far enough ahead that the
       notice check passes rather than failing on the demonstration row. */
    { who: 0, kind: 'holiday', ...soon(70, 12), state: 'asked' },
  ];

  plan.forEach((p, i) => {
    /* Same shape as the diary's demo day: a roster of nought makes this
       `p.who % 0`, which is NaN, and `roster[NaN]` is undefined. Guarded at
       the top of `seedLeave` as well, and again here so the guard cannot be
       lost by an edit up there. */
    const person = roster.length ? roster[p.who % roster.length] : null;
    if (!person) return;
    leave.rows.push({
      id: 'L' + (i + 1) + '-' + businessId,
      staffId: person.id,
      staff: person.name,
      kind: p.kind,
      from: p.from,
      to: p.to,
      state: p.state || 'recorded',
      note: null,
      at: new Date(p.from + 'T09:00:00').toISOString(),
      by: p.kind === 'holiday' ? 'business' : 'staff',
      demo: true,
    });
  });

  /* An employment record for everybody, so the balances have something to
     stand on. The start dates are far enough back that the qualifying
     period is met — a roster where nobody may use egenmelding would show
     the exception rather than the rule. */
  roster.forEach((p, i) => {
    leave.staff[p.id] = {
      ...EMPLOYMENT,
      employedSince: `${new Date().getFullYear() - 2 - (i % 3)}-03-01`,
      /* One person over sixty on a roster of four, so the extra week in
         ferieloven § 5 (2) is visible on a screen somewhere rather than
         only in a test. */
      born: i === 0 ? new Date().getFullYear() - 61 : null,
      children: [2, 0, 1, 3][i % 4],
      soleCarer: i % 4 === 2,
      demo: true,
    };
  });

  write(s);
  return leave;
}

/**
 * Everything the absence screens read.
 *
 * `staffId` narrows it to one person, which is the difference between the
 * business surface and the staff surface — and it is narrowed HERE, not by
 * a filter in a component, for the same reason the diary is.
 */
export function fetchLeave(businessId, staffId = null) {
  const s = state();
  const leave = seedLeave(s, businessId);
  const rows = (leave.rows || [])
    .filter(r => !staffId || r.staffId === staffId)
    .sort((a, b) => (b.from || '').localeCompare(a.from || ''));

  const roster = peopleAt(businessId);
  const b = catalogue().find(x => x.id === businessId) || {};

  return wait({
    businessId,
    rows,
    people: roster.map(p => ({
      id: p.id, name: p.name, title: p.title,
      employment: employmentOf(leave, p.id),
    })),
    settings: {
      /* The IA arrangement. Off unless the business says otherwise, because
         the statute is the floor and an arrangement above it is something
         somebody agreed with the tillitsvalgte — not something a platform
         may assume. */
      extendedSelf: !!leave.settings.extendedSelf,
      /* Five weeks is a tariffavtale or a contract. Null means "nothing
         agreed", which is not the same as "four weeks and a day". */
      agreedHoliday: leave.settings.agreedHoliday ?? null,
      country: leave.settings.country || b.country || 'NO',
    },
    demo: rows.filter(r => r.demo).length,
  });
}

/** The business's own settings: the IA arrangement and the agreed holiday. */
export function saveLeaveSettings(businessId, patch = {}) {
  const s = state();
  const leave = seedLeave(s, businessId);
  if ('extendedSelf' in patch) leave.settings.extendedSelf = !!patch.extendedSelf;
  if ('agreedHoliday' in patch) {
    const n = Number(patch.agreedHoliday);
    leave.settings.agreedHoliday = Number.isFinite(n) && n > 0 ? n : null;
  }
  if ('country' in patch) leave.settings.country = patch.country || 'NO';
  write(s);
  return fetchLeave(businessId);
}

/** One person's employment record, as the employer holds it. */
export function saveEmployment(businessId, staffId, patch = {}) {
  const s = state();
  const leave = seedLeave(s, businessId);
  const now = { ...employmentOf(leave, staffId), ...patch };
  leave.staff[staffId] = {
    employedSince: now.employedSince || null,
    born: now.born ? Number(now.born) : null,
    children: Math.max(0, Number(now.children) || 0),
    soleCarer: !!now.soleCarer,
    /* A boolean about a decision, never a word about a condition. */
    chronic: !!now.chronic,
    rosteredPerWeek: Math.max(0, Number(now.rosteredPerWeek) || 0),
  };
  write(s);
  return fetchLeave(businessId);
}

/**
 * A notification: egenmelding, a sick child, or a sykmelding being recorded.
 *
 * It is written as final. There is no state for "waiting", because there is
 * nobody waiting — the employee has told their employer a fact. What the
 * caller gets back is the row and, where the statutory ceiling cuts it
 * short, the days that fell outside it and the sentence saying a sykmelding
 * covers those instead.
 */
export function reportAbsence(businessId, staffId, input = {}) {
  const s = state();
  const leave = seedLeave(s, businessId);
  const kind = input.kind;
  if (!['self', 'child', 'sick'].includes(kind)) {
    return fail('That is not something you report — it is something you ask for.');
  }
  const person = peopleAt(businessId).find(p => p.id === staffId);
  if (!person) return fail('Nobody by that name is on this roster.');

  const from = String(input.from || '').slice(0, 10);
  const to = String(input.to || from).slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(from) || to < from) {
    return fail('Those dates do not make a stretch of days.');
  }

  const country = leave.settings.country || 'NO';
  let keepFrom = from;
  let keepTo = to;
  let note = null;

  /* Only egenmelding has a ceiling on how long one absence may be. A
     sykmelding is as long as the doctor wrote, and omsorgsdager are counted
     against the year rather than capped per occasion. */
  if (kind === 'self') {
    const employment = employmentOf(leave, staffId);
    const allowed = selfAllowed(
      { rows: leave.rows.filter(r => r.staffId === staffId), ...employment,
        extended: !!leave.settings.extendedSelf },
      from, country,
    );
    if (!allowed.may) return fail(allowed.why);
    const trimmed = trimSelf(from, to, allowed, country);
    keepFrom = trimmed.from;
    keepTo = trimmed.to;
    note = trimmed.say;
  }

  const row = {
    id: newId('L'),
    staffId,
    staff: person.name,
    kind,
    from: keepFrom,
    to: keepTo,
    state: 'recorded',
    note: input.note || null,
    at: new Date().toISOString(),
    by: 'staff',
  };
  leave.rows.unshift(row);
  write(s);
  return wait({ row, trimmed: note });
}

/**
 * An ask, and the only one of the four that is one.
 *
 * The employer decides holiday (ferieloven § 6). What this records is the
 * employee's wish, and what `decideHoliday` records is the answer — with the
 * notice that was actually given attached to it, because two months is what
 * the Act asks for and the screen has to be able to say whether it was met.
 */
export function askForHoliday(businessId, staffId, input = {}) {
  const s = state();
  const leave = seedLeave(s, businessId);
  const person = peopleAt(businessId).find(p => p.id === staffId);
  if (!person) return fail('Nobody by that name is on this roster.');

  const from = String(input.from || '').slice(0, 10);
  const to = String(input.to || from).slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(from) || to < from) {
    return fail('Those dates do not make a stretch of days.');
  }

  const row = {
    id: newId('L'),
    staffId,
    staff: person.name,
    kind: input.kind === 'leave' ? 'leave' : 'holiday',
    from, to,
    state: 'asked',
    note: input.note || null,
    at: new Date().toISOString(),
    by: 'staff',
  };
  leave.rows.unshift(row);
  write(s);
  return wait({ row });
}

/**
 * The employer's answer.
 *
 * Refuses outright to touch a notification. This is the one place where the
 * two mechanisms could be quietly collapsed into one — a single "approve"
 * endpoint taking any row id — and it is the one place it matters most that
 * they are not.
 */
export function decideHoliday(businessId, rowId, decision, note = null) {
  const s = state();
  const leave = seedLeave(s, businessId);
  const row = (leave.rows || []).find(r => r.id === rowId);
  if (!row) return fail('There is no such request.');
  if (!needsApproval(row.kind)) {
    return fail(
      'An egenmelding or a day with an ill child is a notification, not an '
      + 'application. There is nothing here to approve or refuse.');
  }
  if (!['agreed', 'refused'].includes(decision)) return fail('That is not an answer.');

  row.state = decision;
  row.decidedAt = new Date().toISOString();
  row.decidedNote = note || null;
  /* What notice was actually given, worked out at the moment of the answer
     and kept on the row — recomputing it later from today's date would say
     something different every time the screen was opened. */
  row.notice = decision === 'agreed'
    ? noticeFor(row.from, String(row.decidedAt).slice(0, 10), leave.settings.country || 'NO')
    : null;
  write(s);
  return wait({ row });
}

/** Taking one back. The employee's own row, and only while it is theirs to
    take back — a holiday already agreed is a plan the business has built a
    rota around, and undoing that is a conversation, not a button. */
export function withdrawAbsence(businessId, rowId) {
  const s = state();
  const leave = seedLeave(s, businessId);
  const row = (leave.rows || []).find(r => r.id === rowId);
  if (!row) return fail('There is no such entry.');
  if (row.kind === 'holiday' && row.state === 'agreed') {
    return fail('This holiday has been agreed and the rota is built around it. '
      + 'Changing it is a conversation with your manager, not a button here.');
  }
  row.state = 'withdrawn';
  write(s);
  return wait({ row });
}

/* ── The demonstration salon ──────────────────────────────────────────────
   Lykke Salong is the business a future tenant is shown, so it gets a fuller
   picture than the thin sample every other business seeds: a fortnight of
   appointments still to come and three months of completed work behind them,
   with the practitioner, the products that went over the counter, the
   payment method the customer chose, and — on the completed ones — the
   sequential reference the sale is known by.

   ── Why the references are consecutive ────────────────────────────────────

   Bokføringsloven §§ 4 nr. 7 and 6 want a trail that runs both ways, and a
   sequential reference is what makes the downward direction possible. A hole
   in the series is what an auditor looks for first, `gaps()` reports one, and
   a demonstration that shipped with one would teach the wrong lesson about
   what a clean set of books looks like.

   ── And none of it is revenue ─────────────────────────────────────────────

   Every record carries `demo: true`, which is what keeps it out of the
   takings and out of the journal — see `counts()` in domain/revenue.js. The
   sales log shows it and says so, because a screen with nothing on it teaches
   nothing; the money screens do not, because a business that found this
   file's figures inside its own revenue would be right to distrust every
   other number in the console. */

function seedSalon(s, businessId) {
  if (businessId !== DEMO_SALON) return;
  s.businesses = s.businesses || {};
  s.businesses[businessId] = s.businesses[businessId] || {};
  if (s.businesses[businessId].salonSeeded) return;
  s.businesses[businessId].salonSeeded = true;

  const b = catalogue().find(x => x.id === businessId);
  const roster = peopleAt(businessId);
  if (!b || !roster.length) return;

  const day = n => {
    const d = new Date();
    d.setDate(d.getDate() + n);
    return d.toISOString().slice(0, 10);
  };
  const person = id => roster.find(p => p.id === id) || roster[0];
  const svc = name => (b.services || []).find(x => x.name === name) || null;

  /* The customers, and the relation that entitles the salon to see them. A
     relation is what the disclosure gate rests on, so a demonstration
     customer without one would be invisible on the business's own screens —
     correctly, and uselessly. */
  demoCustomers.forEach((c, i) => {
    const uid = normalise(c.phone);
    s.customers[uid] = s.customers[uid] || {
      uid, phone: uid, name: c.name, source: 'manual', verified: false,
      created: day(-200), demo: true,
    };
    s.relations[uid] = s.relations[uid] || [];
    if (!s.relations[uid].some(r => r.businessId === businessId)) {
      s.relations[uid].push({
        businessId, industry: b.industry, since: day(-200),
        number: new Date().getFullYear() + '-' + (2000 + i * 3), by: 'customer',
        demo: true,
      });
    }
    s.records[uid] = s.records[uid] || [];
  });

  const write1 = (c, row) => {
    const uid = normalise(demoCustomers[c].phone);
    s.records[uid].unshift(row);
  };

  /* Still to come. The payment method is the one the customer CHOSE at
     booking; `held` is whether the amount was authorised then. Those are two
     different facts and the screens keep them apart — an authorisation is
     still the customer's money. */
  demoAhead.forEach((a, i) => {
    const sv = svc(a.svc[0]);
    if (!sv) return;
    const who = person(a.with);
    const when = `${day(a.in)} ${a.at}`;
    const products = a.products || [];
    write1(a.who, {
      id: 'DA' + (i + 1),
      businessId,
      type: 'agreement',
      title: words(b.industry).appointment_doc,
      subtitle: `${sv.name} · ${a.at}`,
      time: when,
      date: shortDate(day(a.in)),
      ts: new Date(when.replace(' ', 'T')).getTime(),
      amount: (sv.price || 0) + products.reduce((n, p) => n + p.price * p.qty, 0),
      status: 'confirmed',
      demo: true,
      meta: {
        service: sv.name,
        service_amount: sv.price || 0,
        minutes: a.svc[2],
        duration: a.svc[2] + ' min',
        payment: a.pay,
        staff: who.name,
        staff_id: who.id,
        staff_title: who.title,
        products: products.map(p => p.id),
        product_lines: products,
        note: a.note || null,
      },
      /* Authorised at booking, not captured. The other half happens when the
         work is carried out. */
      auth: a.held
        ? { state: 'reserved', amount: sv.price || 0, method: a.pay,
            at: new Date().toISOString() }
        : null,
      body: a.note || `${sv.name} with ${who.name}.`,
    });
  });

  /* What has already happened, with its reference. */
  demoBehind.forEach((h, i) => {
    const sv = svc(h.svc[0]);
    if (!sv) return;
    const who = person(h.with);
    const when = `${day(-h.ago)} ${h.at}`;
    const products = h.products || [];
    const total = (sv.price || 0) + products.reduce((n, p) => n + p.price * p.qty, 0);
    write1(h.who, {
      id: 'DH' + (i + 1),
      businessId,
      type: 'visit',
      title: words(b.industry).visit || 'Completed appointment',
      subtitle: `${sv.name} · ${who.name}`,
      time: when,
      date: shortDate(day(-h.ago)),
      ts: new Date(when.replace(' ', 'T')).getTime(),
      amount: total,
      status: 'done',
      demo: true,
      /* The reference the sale is known by. Sequential, per year, and the
         thing a report points back down at. */
      ref: salesRef(new Date(when).getFullYear(), h.seq),
      meta: {
        service: sv.name,
        service_amount: sv.price || 0,
        duration: h.svc[2] + ' min',
        payment: h.pay,
        staff: who.name,
        staff_id: who.id,
        staff_title: who.title,
        products: products.map(p => p.id),
        product_lines: products,
      },
      body: `${sv.name} with ${who.name}.`,
    });
  });

  write(s);
}

/**
 * The sales log: every completed piece of work, as a document.
 *
 * This is the half of the audit trail that runs downwards — a report totals
 * these and every row carries the reference that leads back to it. See
 * domain/salesdoc.js.
 */
export function fetchSales(businessId, fromISO, toISO) {
  const s = state();
  seedSalon(s, businessId);
  const b = catalogue().find(x => x.id === businessId);
  if (!b) return fail('No such business.');
  const shown = merged(b) || b;
  const vatOf = (s.businesses?.[businessId]?.serviceVat) || {};

  const out = [];
  Object.entries(s.records).forEach(([uid, rows]) => rows.forEach(r => {
    if (r.businessId !== businessId) return;
    if (r.type !== 'visit') return;
    const at = String(r.time || '');
    const d = at.slice(0, 10);
    if (fromISO && (d < fromISO || d > toISO)) return;
    const c = s.customers[uid];
    out.push({
      id: r.id,
      ref: r.ref || null,
      at,
      staff: r.meta?.staff || null,
      staffId: r.meta?.staff_id || null,
      /* The same gate as everywhere else: the business sees the fields its
         trade may be told, and no more. */
      customer: c ? disclosedTo(c, b, true) : null,
      service: r.meta?.service || r.title,
      serviceAmount: r.meta?.service_amount ?? r.amount ?? 0,
      serviceVat: vatOf[r.meta?.service] || null,
      products: (r.meta?.product_lines || []).map(p => ({
        ...p, vat: vatOf[p.name] || null,
      })),
      paid: r.meta?.payment || null,
      amount: r.amount ?? 0,
      demo: !!r.demo,
    });
  }));

  out.sort((a, c) => String(c.at).localeCompare(String(a.at)));
  return wait({
    businessId,
    business: { name: b.name, org_no: shown.org_no || null,
                vat_registered: !!shown.vat_registered },
    from: fromISO || null,
    to: toISO || null,
    sales: out,
    demo: out.filter(x => x.demo).length,
  });
}

/* ── The social planner ───────────────────────────────────────────────────
   Posts the business writes, the channels they are going to and the day they
   go. Kept on the business, because that is whose they are.

   ── Only one channel is real ──────────────────────────────────────────────

   `page` is this platform's own page band and can genuinely be published to.
   Instagram, Facebook, TikTok and Snapchat need an app registered with them,
   reviewed and approved, and none of that exists — so `publishPost` writes
   to the page and writes NOTHING anywhere else, and says which. A planner
   that quietly marked four channels "posted" would be the worst kind of
   lie: one nobody notices until a customer asks why they never saw it.

   ── And a planned post is not a published one ─────────────────────────────

   The same distinction as the rota. A post sitting on the calendar commits
   nothing; publishing puts it in front of people. Two verbs, and the time
   passing is a third state again — `held` — rather than a silent failure. */

function socialOf(s, businessId) {
  s.businesses = s.businesses || {};
  s.businesses[businessId] = s.businesses[businessId] || {};
  const b = s.businesses[businessId];
  /* `plan`, NOT `social`. The Website tab already saves the tenant's social
     HANDLES under `social` — `{instagram: {handle, on}, …}` — and `merged()`
     spreads everything here over the business record. Keeping the planner's
     posts under the same key glued a `posts` array and a `seeded` flag onto
     the handles object: the Website tab's dirty-check then compared the
     handles against a shape that could never match again and showed unsaved
     changes for ever, and `fetchPlan` handed the screen a handles map with
     the whole planner inside it.
     The same collision as `services` and `serviceVat`. Its own key. */
  b.plan = b.plan || { posts: [], seeded: false };
  b.plan.posts = b.plan.posts || [];
  return b.plan;
}

/* A month with something in it, so the calendar is not an empty grid on a
   browser that has never been used. Three posts, spread, every one marked
   `demo: true` and said so on the screen — the same terms as the sample rota
   and the sample absences. */
function seedSocial(s, businessId) {
  const plan = socialOf(s, businessId);
  if (plan.seeded) return plan;
  plan.seeded = true;

  const b = catalogue().find(x => x.id === businessId);
  if (!b) return plan;
  const ind = industry(b.industry);
  const svc = (b.services || [])[0];

  const at = (days, time) => {
    const d = new Date();
    d.setDate(d.getDate() + days);
    return d.toISOString().slice(0, 10) + ' ' + time;
  };

  [
    { d: 2, t: '10:00', text: `Free times this week at ${b.name}.`
        + (svc ? ` ${svc.name} — book straight from the page.` : ''),
      ch: ['page', 'instagram'] },
    { d: 6, t: '17:30', text: 'Behind the scenes today. #' + (ind.search?.[0] || 'avtale'),
      ch: ['instagram', 'facebook'] },
    { d: 13, t: '09:00', text: 'A new month, and the book is open.',
      ch: ['page', 'facebook', 'tiktok'] },
  ].forEach((x, i) => {
    plan.posts.push({
      id: 'SP' + (i + 1) + '-' + businessId,
      text: x.text,
      image: null,
      ratio: null,
      channels: x.ch,
      when: at(x.d, x.t),
      state: 'planned',
      made: new Date().toISOString(),
      demo: true,
    });
  });
  write(s);
  return plan;
}

/* ── Brønnøysundregistrene ────────────────────────────────────────────────
   The open register, called straight from the browser.

   ── Why this one does NOT wait for Chamcore ──────────────────────────────

   Every other piece of real data on this platform waits for a backend,
   because every other piece needs a credential we do not have. This one needs
   nothing: Enhetsregisteret's open API takes no key, no registration and no
   agreement, and it answers to anybody. There is no reason to put a tenant's
   organisation number behind a server that does not exist yet when the
   register will answer the browser today.

   The one thing that could stop it is CORS, and it is not stated in the
   register's documentation — so this is written to find out at runtime
   rather than to assume. If the browser refuses the cross-origin request the
   error says exactly that, and names the fix: the same call from Chamcore,
   which is `api/chamcore.js` and the route documented in docs/GAP.md. Either
   way the tenant is told something true instead of "lookup failed".

   Nothing is cached. A business looks its own number up once. */
const BRREG_WAIT = 12000;

async function brregGet(path) {
  /* A register that is slow is a register the tenant waits for forever
     unless somebody sets a limit, and a spinner with no end is the worst of
     the failure modes. */
  const stop = new AbortController();
  const timer = setTimeout(() => stop.abort(), BRREG_WAIT);
  try {
    const r = await fetch(BRREG_BASE + path, {
      headers: { Accept: 'application/json' },
      signal: stop.signal,
    });
    if (r.status === 404) return null;
    if (!r.ok) {
      throw new Error(`Brønnøysundregistrene answered ${r.status}. `
        + 'That is their end, not yours — try again in a moment.');
    }
    return await r.json();
  } catch (err) {
    if (err?.name === 'AbortError') {
      throw new Error('Brønnøysundregistrene did not answer in time. '
        + 'Try again, or type the details in yourself.');
    }
    /* A cross-origin refusal arrives as an ordinary TypeError with nothing
       useful in it, so the sentence has to be written here. Saying "check
       your connection" would send the tenant to look at the wrong thing. */
    if (err instanceof TypeError) {
      throw new Error('This browser would not let the page call '
        + 'Brønnøysundregistrene directly. The register is open and the call '
        + 'is correct — it needs to be made from our own server instead. '
        + 'Type the details in for now.');
    }
    throw err;
  } finally {
    clearTimeout(timer);
  }
}

/**
 * Look a business up by name or by number.
 *
 * Answers a LIST, even for a single number, because the screen shows the same
 * thing either way and a caller that has to handle two shapes is a caller
 * that will handle one of them wrong.
 */
export async function lookupOrg(input) {
  const q = brregQuery(input);
  if (!q) return { query: '', hits: [] };
  const body = await brregGet(q.path);
  return {
    query: String(input).trim(),
    kind: q.kind,
    hits: brregEntities(body).map(readEntity),
  };
}

/**
 * Who runs it, from the register's own roles endpoint.
 *
 * Separate from the look-up because it is a second request and most of the
 * time nobody needs it — and because a roles endpoint that is down must not
 * take the organisation number down with it.
 */
export async function lookupRoles(orgNo) {
  const d = String(orgNo || '').replace(/\D/g, '');
  if (!/^\d{9}$/.test(d)) return fail('An organisation number is nine digits.');
  const body = await brregGet(`/enheter/${d}/roller`);
  return { orgNo: d, people: readRoles(body || {}) };
}

/* ── The business's own pictures ──────────────────────────────────────────
   Stein: "dette skal jo utelukkende skje i den grad mulig på brukerens
   maskin, men om dette må lastes opp på vår server så kan det bli mye load.
   Unngå det" — and then, once the shape was clear: point at our server, use
   dummy data until Nouman connects it.

   So that is exactly what this is. `api/chamcore.js` posts the file to our
   own media endpoint and is the real implementation; this adapter answers
   the same shape without a server behind it.

   ── Why the bytes live in memory and not in localStorage ────────────────

   Because a twelve-megabyte photograph as a data URL is about sixteen
   megabytes of string, and localStorage gives a browser around five in
   total. Writing one there does not fail politely: it throws QuotaExceeded
   part-way through `write(s)`, and the state that was being saved — the
   tenant's posts, their price list, their customers — is what gets lost.
   That is the same class of bug as the empty price list that took the whole
   console to a white page, and the fix is not to be cleverer about it but
   not to do it.

   So an upload here is an object URL held for the life of the page, and the
   screen says in as many words that it will not survive a reload until the
   server is connected. A dummy that pretended otherwise would be lying about
   the one thing the tenant would notice.

   The dummy rows are the platform's own pack, marked `demo: true`, so the
   library is not an empty box on the first open. */
const uploaded = new Map();

const mediaRow = (businessId, extra) => ({
  id: 'MED' + Math.random().toString(36).slice(2, 9),
  businessId,
  at: new Date().toISOString(),
  ...extra,
});

/** Dummy rows: the trade's own pack, so the library opens with something in it. */
function dummyMedia(businessId) {
  const b = catalogue().find(x => x.id === businessId);
  if (!b) return [];
  const shots = (pack(b.industry) || []).slice(0, 8);
  return shots.map((sh, i) => mediaRow(businessId, {
    id: 'DEMO-' + (sh.id || i),
    url: urlOf(sh),
    kind: 'image',
    name: sh.brief || 'From the pack',
    bytes: null,
    /* Not the tenant's file. Marked, and every screen that lists these says
       how many — the same rule as every other piece of demonstration data on
       this platform. */
    demo: true,
    at: null,
  }));
}

/**
 * The pictures and clips this business has, newest first.
 *
 * Its own uploads before the pack, because a tenant who has just uploaded
 * something is looking for it and not for a stock photograph.
 */
export function fetchMedia(businessId) {
  const mine = [...(uploaded.get(businessId) || [])]
    .sort((a, b) => String(b.at).localeCompare(String(a.at)));
  return wait({
    businessId,
    items: [...mine, ...dummyMedia(businessId)],
    mine: mine.length,
    /* Said plainly by the screen rather than discovered by the tenant when
       their picture is gone. */
    kept: false,
    keptWhy: 'Until the media server is connected, a file you upload is held '
      + 'for as long as this page is open and no longer. Nothing is sent '
      + 'anywhere, and a reload clears it.',
  });
}

/**
 * Take a file.
 *
 * The check is domain/media.js and not this file, so the form can refuse a
 * HEIC before the upload starts and refuse it in the same words this would.
 */
export function uploadMedia(businessId, file) {
  const b = catalogue().find(x => x.id === businessId);
  if (!b) return fail('No such business.');
  const check = checkFile(file);
  if (!check.ok) return fail(check.why);

  let url;
  try {
    url = URL.createObjectURL(file);
  } catch {
    return fail('This browser would not open that file.');
  }

  const row = mediaRow(businessId, {
    url,
    kind: check.kind,
    name: file.name || 'Untitled',
    bytes: file.size || null,
    /* A real file from the tenant — NOT demonstration data, even though the
       adapter behind it is. The flag describes the picture, not the server. */
    demo: false,
  });
  uploaded.set(businessId, [...(uploaded.get(businessId) || []), row]);
  return wait(row);
}

/**
 * A composed picture, as the studio produced it.
 *
 * Kept apart from `uploadMedia` because it is a different act: the tenant
 * made this one rather than chose it, and it carries the recipe that made it
 * so the studio can be reopened on it instead of starting again.
 */
export function saveComposed(businessId, dataUrl, recipe = {}) {
  const b = catalogue().find(x => x.id === businessId);
  if (!b) return fail('No such business.');
  if (!String(dataUrl || '').startsWith('data:image/'))
    return fail('That is not a picture this build produced.');

  const row = mediaRow(businessId, {
    url: dataUrl,
    kind: 'image',
    name: 'Composed',
    bytes: Math.round(String(dataUrl).length * 0.75),
    composed: true,
    recipe,
    demo: false,
  });
  uploaded.set(businessId, [...(uploaded.get(businessId) || []), row]);
  return wait(row);
}

/** Forget one. The pack cannot be deleted — it is not the tenant's to delete. */
export function deleteMedia(businessId, id) {
  const list = uploaded.get(businessId) || [];
  const row = list.find(x => x.id === id);
  if (!row) return fail('That picture is part of the platform pack and is not yours to remove.');
  try { if (String(row.url).startsWith('blob:')) URL.revokeObjectURL(row.url); } catch { /* gone already */ }
  uploaded.set(businessId, list.filter(x => x.id !== id));
  return wait({ ok: true });
}

export function fetchPlan(businessId) {
  const s = state();
  const plan = seedSocial(s, businessId);
  const b = catalogue().find(x => x.id === businessId) || {};
  return wait({
    businessId,
    business: b.name,
    posts: (plan.posts || []).slice()
      .sort((a, c) => String(a.when || '9').localeCompare(String(c.when || '9'))),
    /* The handles the tenant typed on the Website tab. A preview showing
       "@yourbusiness" where a real handle exists is a preview of somebody
       else's post. */
    handles: (merged(b) || {}).social || {},
    demo: (plan.posts || []).filter(p => p.demo).length,
  });
}

export function savePost(businessId, input = {}) {
  const s = state();
  const plan = seedSocial(s, businessId);
  const now = new Date().toISOString();

  if (input.id) {
    const row = plan.posts.find(p => p.id === input.id);
    if (!row) return fail('There is no such post.');
    if (row.state === 'posted') {
      return fail('This one is already out. A post that has been published is '
        + 'not something this screen can rewrite.');
    }
    Object.assign(row, {
      text: input.text ?? row.text,
      image: input.image === undefined ? row.image : input.image,
      /* Whether the attachment is a picture or a clip. Stored rather than
         guessed from the URL: a blob: address says nothing about what is
         behind it, and a preview that guessed wrong would draw a still frame
         for a video and an empty player for a photograph. */
      kind: input.kind === undefined ? (row.kind || 'image') : input.kind,
      ratio: input.ratio === undefined ? row.ratio : input.ratio,
      channels: input.channels ?? row.channels,
      when: input.when ?? row.when,
      state: input.when ? 'planned' : 'draft',
      edited: now,
      /* Edited by the tenant, so it is theirs now rather than ours. */
      demo: false,
    });
    write(s);
    return wait({ post: row });
  }

  const row = {
    id: newId('SP'),
    text: input.text || '',
    image: input.image || null,
    kind: input.kind || 'image',
    ratio: input.ratio || null,
    channels: input.channels || [],
    when: input.when || '',
    state: input.when ? 'planned' : 'draft',
    made: now,
  };
  plan.posts.unshift(row);
  write(s);
  return wait({ post: row });
}

export function deletePost(businessId, postId) {
  const s = state();
  const plan = seedSocial(s, businessId);
  const before = plan.posts.length;
  plan.posts = plan.posts.filter(p => p.id !== postId);
  if (plan.posts.length === before) return fail('There is no such post.');
  write(s);
  return fetchPlan(businessId);
}

/**
 * Publishing.
 *
 * To the page, really. To the other four, not at all — and the answer says
 * so rather than the screen assuming. The page band takes three posts, which
 * is a limit worth keeping: a page with nine campaigns on it has no campaign
 * on it, so the newest three win and the answer says what was dropped.
 */
export function publishPost(businessId, postId) {
  const s = state();
  const plan = seedSocial(s, businessId);
  const row = plan.posts.find(p => p.id === postId);
  if (!row) return fail('There is no such post.');

  const real = (row.channels || []).filter(c => c === 'page');
  const not = (row.channels || []).filter(c => c !== 'page');

  if (real.length) {
    s.businesses[businessId] = s.businesses[businessId] || {};
    const own = s.businesses[businessId];
    const b = catalogue().find(x => x.id === businessId) || {};
    const had = own.posts || b.posts || [];
    const line = {
      title: String(row.text || '').split(/[.!?\n]/)[0].slice(0, 90),
      text: String(row.text || '').slice(0, 240),
      tag: 'Campaign',
    };
    own.posts = [line, ...had.filter(p => p.title !== line.title)].slice(0, 3);
  }

  row.state = real.length ? 'posted' : 'held';
  row.postedAt = new Date().toISOString();
  write(s);
  return wait({
    post: row,
    onPage: real.length > 0,
    notSent: not,
    say: not.length
      ? `Put on your own page. Nothing was sent to `
        + `${not.join(', ')} — those are not connected in this build.`
      : 'Put on your own page.',
  });
}

/* ── Occupational health ──────────────────────────────────────────────────
   The business subscribes to a provider; its people book their own
   appointments against it.

   ── The gate, and why it is here rather than in a component ───────────────

   A bedriftshelsetjeneste is confidential. What somebody consults it about
   is between them and the health personnel, and the employer — who pays for
   it — is not told. So this file NEVER hands the business surface a booking
   row. `fetchOccHealth` takes a `staffId`: with one it answers that person's
   own bookings in full, and without one it answers `employerView`, which is
   counts and nothing else.

   The narrowing is on this side of the seam, exactly like the customer
   disclosure gate, because a component that received the rows and promised
   to render three fields is one refactor away from rendering four. */

function healthOf(s, businessId) {
  s.businesses = s.businesses || {};
  s.businesses[businessId] = s.businesses[businessId] || {};
  const b = s.businesses[businessId];
  b.health = b.health || { sub: { state: 'none', provider: null }, bookings: [] };
  b.health.bookings = b.health.bookings || [];
  return b.health;
}

/**
 * What each side may see.
 *
 * `staffId` is not a filter the caller may forget: without it the bookings
 * do not leave this function at all.
 */
export function fetchOccHealth(businessId, staffId = null) {
  const s = state();
  const h = healthOf(s, businessId);
  const b = catalogue().find(x => x.id === businessId) || {};
  const leave = leaveOf(s, businessId);
  const country = leave.settings.country || b.country || 'NO';
  const roster = peopleAt(businessId);

  return wait({
    businessId,
    country,
    duty: occDuty(b.industry, country),
    sub: h.sub,
    heads: roster.length,
    /* Two answers, and only ever one of them. */
    mine: staffId ? occMyView(h.bookings, staffId) : null,
    use: staffId ? null : occEmployerView(h.bookings, country),
  });
}

/** Asking a provider. Nothing is live until they answer — which in this
    build nothing does, and the screen says so rather than flipping to
    active because a button was pressed. */
export function askProvider(businessId, providerId) {
  const s = state();
  const h = healthOf(s, businessId);
  if (!occProvider(providerId)) return fail('There is no such provider.');
  h.sub = {
    state: 'asked',
    provider: providerId,
    asked: new Date().toISOString(),
    since: null,
    /* No price. Nobody has been asked for one, and a figure written here
       would come back out of a screen looking like a quote. */
    price: null,
  };
  write(s);
  return fetchOccHealth(businessId);
}

/**
 * The provider saying yes.
 *
 * In production nothing in a browser may do this: the provider accepts, at
 * their end, and tells our backend. It is a button here so the rest of the
 * road can be walked, and the screen that shows it says it is the one thing
 * on it that would not exist in the real thing — the same arrangement as
 * standing in for Stripe's webhook.
 */
export function providerAccepts(businessId) {
  const s = state();
  const h = healthOf(s, businessId);
  if (h.sub?.state !== 'asked') return fail('Nobody has been asked yet.');
  h.sub = { ...h.sub, state: 'live', since: new Date().toISOString().slice(0, 10) };
  write(s);
  return fetchOccHealth(businessId);
}

export function endProvider(businessId) {
  const s = state();
  const h = healthOf(s, businessId);
  if (!h.sub?.provider) return fail('There is nothing to end.');
  h.sub = { ...h.sub, state: 'ended', ended: new Date().toISOString().slice(0, 10) };
  write(s);
  return fetchOccHealth(businessId);
}

/**
 * An employee booking for themselves.
 *
 * The row is written with the person on it, because it is their record. It
 * is simply never handed to the business — see `fetchOccHealth`.
 */
export function bookOccHealth(businessId, staffId, input = {}) {
  const s = state();
  const h = healthOf(s, businessId);
  if (!occUsable(h.sub)) {
    return fail('There is no active occupational health service to book against.');
  }
  const person = peopleAt(businessId).find(p => p.id === staffId);
  if (!person) return fail('Nobody by that name is on this roster.');

  const can = occBookable(h.sub.provider, input.service, input.way);
  if (!can.may) return fail(can.why);

  const date = String(input.date || '').slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(date)) return fail('Pick a day.');

  const row = {
    id: newId('H'),
    staffId,
    businessId,
    provider: h.sub.provider,
    service: input.service,
    way: input.way,
    at: date,
    time: input.time || null,
    mins: can.mins,
    state: 'booked',
    /* A room only where it is a video appointment, and the same room model
       the rest of the platform uses rather than a second one. */
    room: input.way === 'video' ? { id: newId('R'), opens: null, demo: true } : null,
    demo: true,
  };
  h.bookings.unshift(row);
  write(s);
  return wait({ row });
}

export function cancelOccHealth(businessId, staffId, bookingId) {
  const s = state();
  const h = healthOf(s, businessId);
  const row = h.bookings.find(b => b.id === bookingId && b.staffId === staffId);
  if (!row) return fail('There is no such appointment.');
  row.state = 'cancelled';
  write(s);
  return wait({ row });
}

/* ── Chat ─────────────────────────────────────────────────────────────────
   One thread model for two quite different conversations, because they are
   the same shape and splitting them would mean two of everything:

     'staff'     the business and one of its people. About shifts, swaps,
                 who is covering Friday.
     'customer'  the business and somebody booking. This is the one the
                 prototype had and this build had lost: a booking made
                 inside the conversation, without leaving it.

   A thread is addressed by `${businessId}|${kind}|${withId}`, so reception
   opening Marte's thread and Marte opening hers are the same thread and not
   two halves of one.

   The disclosure rule does not get an exception for chat. A customer thread
   exists because that customer booked — the relation is what creates it —
   and a business cannot open a thread with somebody it has no relation to.
   A chat window would otherwise be a way of reaching a name the CRM is not
   allowed to show. */
const threadKey = (businessId, kind, withId) => `${businessId}|${kind}|${withId}`;

export function fetchThread(businessId, kind, withId) {
  const s = state();
  s.threads = s.threads || {};
  const key = threadKey(businessId, kind, withId);
  return wait({ key, messages: s.threads[key] || [] });
}

/** Every thread a business has, for the list down the side. */
export function fetchThreads(businessId) {
  const s = state();
  const roster = peopleAt(businessId);
  const out = [];

  roster.forEach(p => {
    const msgs = (s.threads || {})[threadKey(businessId, 'staff', p.id)] || [];
    out.push({
      kind: 'staff', withId: p.id, name: p.name, role: p.title || p.role,
      last: msgs[msgs.length - 1] || null,
      unread: msgs.filter(m => m.from === 'them' && !m.seen).length,
    });
  });

  /* Customers, and only the ones with a relation — the same gate as the CRM,
     applied here so a chat list cannot become a back door into it. */
  const b = catalogue().find(x => x.id === businessId);
  Object.entries(s.relations || {}).forEach(([uid, rows]) => {
    const rel = rows.find(r => r.businessId === businessId);
    if (!rel) return;
    const fields = rel.by === 'business'
      ? { name: rel.own?.name || '(not given)' }
      : disclosedTo(s.customers[uid], b, true);
    if (!fields) return;
    const msgs = (s.threads || {})[threadKey(businessId, 'customer', uid)] || [];
    out.push({
      kind: 'customer', withId: uid, name: fields.name,
      role: `${words(b.industry).customer} since ${rel.since}`,
      last: msgs[msgs.length - 1] || null,
      unread: msgs.filter(m => m.from === 'them' && !m.seen).length,
    });
  });

  return wait(out.sort((a, b2) =>
    String(b2.last?.at || '').localeCompare(String(a.last?.at || ''))));
}

export function sendMessage(businessId, kind, withId, text, from = 'us', extra = {}) {
  const body = String(text || '').trim();
  if (!body && !extra.booking) throw new Error('An empty message is not a message.');
  const s = state();
  s.threads = s.threads || {};
  const key = threadKey(businessId, kind, withId);
  s.threads[key] = s.threads[key] || [];
  s.threads[key].push({
    id: newId('M'),
    at: new Date().toISOString(),
    from,
    text: body,
    ...extra,
  });
  write(s);
  return wait({ key, messages: s.threads[key] });
}

export function markSeen(businessId, kind, withId) {
  const s = state();
  const key = threadKey(businessId, kind, withId);
  (s.threads?.[key] || []).forEach(m => { if (m.from === 'them') m.seen = true; });
  write(s);
  return wait(true);
}

/**
 * A booking made inside the conversation.
 *
 * The prototype's `chat-book-card`: pick the treatment, pick who and when,
 * authorise. It goes through `confirmBooking` like every other booking —
 * same validation, same hold, same confirmation, same customer account —
 * because a booking made in a chat window is a booking, and a second code
 * path for it is how two kinds of appointment end up with two sets of rules.
 *
 * What chat adds is only the transcript: the agreement is dropped into the
 * thread so the conversation shows what was agreed, where it was agreed.
 */
export async function bookInChat(businessId, withId, booking) {
  const answer = await confirmBooking({ ...booking, businessId, phone: booking.phone || withId });
  await sendMessage(businessId, 'customer', normalise(booking.phone || withId), '', 'us', {
    booking: {
      recordId: answer.record.id,
      service: answer.record.meta?.service,
      staff: answer.record.meta?.staff,
      time: answer.record.time,
      amount: answer.record.amount,
      state: answer.record.auth?.state || null,
    },
  });
  return answer;
}

/* ── The client's own area on a tenant's site ─────────────────────────────
   Everything this person has with THIS business, and nothing they have with
   anybody else. The relation is what makes the area exist — the same gate as
   the CRM, seen from the other side.

   ── What this is actually replacing ───────────────────────────────────────

   Email. Today a client's case lives in an inbox: an extract attached to one
   message, the question about it three messages later, the revised version
   somewhere below that, and no way to find any of it six weeks on. The
   attachment and the sentence that came with it were sent together and are
   then filed apart — which is why "can you resend that document" is a thing
   lawyers spend their afternoons doing.

   So the papers are NOT a list beside the conversation. They are IN it, in
   time order, because that is how it happened: the extract and the sentence
   explaining it arrived together and belong together. What the inbox cannot
   do is find them again, so that is what this adds on top — one stream, a
   filter down to just the papers, and a search across both. Anything that
   split them back into two stores would be rebuilding the inbox with nicer
   typography. */
export function fetchClientArea(businessId, phone) {
  const uid = normalise(phone);
  const business = merged(catalogue().find(x => x.id === businessId));
  if (!business || !uid) return wait(null);

  const s = state();
  const rel = (s.relations[uid] || []).find(r => r.businessId === businessId);
  if (!rel) return wait(null);            /* no relation, no area. Ever. */

  const mine = (s.records[uid] || []).filter(r => r.businessId === businessId);
  const now = new Date().toISOString().slice(0, 16).replace('T', ' ');

  return wait({
    business,
    relation: rel,
    customer: s.customers[uid] || null,
    /* Ahead and behind, because they answer different questions and a single
       list sorted by date makes somebody hunt for the one that matters. */
    upcoming: mine
      .filter(r => r.type === 'agreement' && r.status !== 'cancelled'
                && String(r.time || '') >= now)
      .sort((a, b) => String(a.time).localeCompare(String(b.time))),
    past: mine
      .filter(r => !(r.type === 'agreement' && r.status !== 'cancelled'
                  && String(r.time || '') >= now))
      .sort((a, b) => String(b.time || '').localeCompare(String(a.time || ''))),
    /* The case, in one stream: what was said and what was sent, in the order
       it happened. `by` is on every paper — a document the client uploaded
       and one the firm issued are not the same kind of thing, and a list
       that showed them identically would invite somebody to think the firm
       had written what they themselves sent in. */
    timeline: caseStream(s, businessId, uid),
  });
}

/**
 * The client puts a paper in.
 *
 * ONE door, and it writes to ONE place. An earlier pass had two: this wrote
 * to a documents list and the data room wrote to a room, so the same paper
 * was in the archive or in the stream depending on which screen put it
 * there. Two stores for one thing is how a client ends up being told a
 * document does not exist by a search that is looking in the other one.
 *
 * So the paper goes into the room — the client's archive — and a line goes
 * into the conversation pointing at it, because the paper and the sentence
 * that came with it arrived together and belong together. The room and the
 * stream are two views of the same documents and neither owns them.
 */
export function uploadDocument(businessId, phone, file) {
  const uid = normalise(phone);
  const business = catalogue().find(x => x.id === businessId);
  if (!business) throw new Error('Unknown business.');

  const s0 = state();
  const rel = (s0.relations[uid] || []).find(r => r.businessId === businessId);
  if (!rel) throw new Error('You are not a client of this business.');

  /* Into the room, which validates the file and grants the firm access. */
  addToRoom(uid, businessId, file, 'customer');

  const s = state();
  const doc = roomOf(s, uid).documents[0];

  s.threads = s.threads || {};
  const key = `${businessId}|customer|${uid}`;
  s.threads[key] = s.threads[key] || [];
  s.threads[key].push({
    id: newId('M'),
    at: doc.at,
    from: 'them',                     /* 'them' from the business's side */
    text: String(file?.note || '').trim(),
    doc,
  });

  write(s);
  return fetchClientArea(businessId, uid);
}

/**
 * The case as one stream.
 *
 * Messages, papers and the records of what was agreed, merged and sorted by
 * when they happened. One pass, one sort, one shape — so the filter down to
 * "just the papers" is a filter over this and not a second query against a
 * second store that could disagree with it.
 */
function caseStream(s, businessId, uid) {
  const out = [];

  /* The papers come from the ROOM, through the thread lines that point at
     them — one store, two views. */
  (s.threads?.[`${businessId}|customer|${uid}`] || []).forEach(m => {
    out.push({
      id: m.id,
      at: m.at,
      kind: m.doc ? 'paper' : 'word',
      by: m.from === 'them' ? 'you' : 'them',
      text: m.text || '',
      doc: m.doc || null,
      booking: m.booking || null,
    });
  });

  /* Papers the business issued arrive as records rather than as messages —
     an agreement, a receipt, a letter. Same stream, same shape. */
  (s.records[uid] || [])
    .filter(r => r.businessId === businessId)
    .forEach(r => {
      out.push({
        id: r.id,
        at: r.time ? String(r.time).replace(' ', 'T') : new Date(r.ts || 0).toISOString(),
        kind: ['doc', 'letter', 'receipt', 'agreement', 'quote'].includes(r.type)
          ? 'paper' : 'event',
        by: 'them',
        text: r.title + (r.subtitle ? ' · ' + r.subtitle : ''),
        record: { id: r.id, type: r.type, title: r.title, date: r.date,
                  amount: r.amount || null, status: r.status || null },
        doc: null,
      });
    });

  return out.sort((a, b) => String(b.at).localeCompare(String(a.at)));
}

/* ── The data room ────────────────────────────────────────────────────────
   The searchable archive of a case, and the access that goes with it.

   Chamcore's document centre keeps documents as `sale_contracts` rows
   against a lead, and shares a note by INSERTING A COPY against another
   user's id. The copy is the part not carried over, and the reason is the
   whole model: a case handed on that way leaves a copy behind and takes one
   along, so the client's papers end up in two places, neither of them
   theirs. Here the archive is the CLIENT'S and a firm holds access to it —
   see domain/dataroom.js.

   Every grant, revoke, transfer and send-out is written to the room's own
   log, which the client can read. Chamcore logs the same through
   `logSaleActivity`; the difference is who gets to read it. */
function roomOf(s, uid) {
  s.rooms = s.rooms || {};
  s.rooms[uid] = s.rooms[uid] || { documents: [], log: [], invites: [] };
  s.rooms[uid].invites = s.rooms[uid].invites || [];
  return s.rooms[uid];
}

function note(s, uid, what) {
  roomOf(s, uid).log.unshift({ id: newId('L'), at: new Date().toISOString(), ...what });
}

/* Whose name goes on a grant. A row that says "hovden" is an id; anybody
   reading an access list needs a name.

   `STAFF` is keyed by business and not a list — calling `.find` on it threw
   inside the map and took the whole room down with it. A person is looked up
   through `peopleAt`, which is what knows the shape. */
const nameOfId = id => catalogue().find(b => b.id === id)?.name
  || catalogue().flatMap(b => peopleAt(b.id)).find(p => p.id === id)?.name
  || id;

/** The whole room, from the client's side: everything, always. */
export function fetchRoom(phone, by = {}) {
  const uid = normalise(phone);
  if (!uid) return wait(null);
  const s = state();
  const room = roomOf(s, uid);

  const nameOf = nameOfId;

  const mine = room.documents.map(d => ({
    ...d,
    kind: kindOf(d),
    access: (d.access || []).map(g => ({
      ...g,
      name: nameOf(g.who),
      byName: g.by ? nameOf(g.by) : null,
    })),
  }));

  return wait({
    documents: findDocs(mine, by),
    total: mine.length,
    /* Questions waiting for an answer. They are the client's to answer and
       nobody else's, so they come with the room rather than arriving as a
       notification somebody can miss. */
    invites: room.invites
      .filter(i => i.state === 'pending')
      .map(i => ({
        ...i,
        name: nameOf(i.who),
        byName: i.by ? nameOf(i.by) : null,
        count: i.docs ? i.docs.length : mine.length,
      })),
    log: room.log.map(l => ({ ...l, whoName: nameOf(l.who), byName: nameOf(l.by) })),
  });
}

/** The same room seen by a firm: only what it has a live grant to. */
export function fetchRoomAsBusiness(businessId, phone, by = {}) {
  const uid = normalise(phone);
  const s = state();
  const rel = (s.relations[uid] || []).find(r => r.businessId === businessId);
  if (!rel) return wait(null);                /* no relation, no room */

  const viewer = { kind: 'business', id: businessId };
  const mine = roomOf(s, uid).documents
    .filter(d => canSee(d, viewer))
    .map(d => ({
      ...d,
      kind: kindOf(d),
      /* Only the LIVE grants, and only on the papers this firm can open.
         Who held access before this firm did is the client's business and
         nobody else's — an incoming firm that could read the closed rows
         would be reading the history of a case it was not on. The client
         sees the whole list, closed rows included, in their own room. */
      access: liveAccess(d).map(g => ({ ...g, name: nameOfId(g.who) })),
    }));

  /* Everybody on the case besides this firm, folded across the papers, so the
     invite panel can say "Sandberg — 3 of 5 documents" rather than repeating
     the same name once per row. */
  const others = new Map();
  mine.forEach(d => d.access.forEach(g => {
    if (g.who === businessId) return;
    const had = others.get(g.who)
      || { who: g.who, name: g.name, role: g.role, from: g.from, about: g.about || null, docs: 0 };
    had.docs += 1;
    others.set(g.who, had);
  }));

  const room = roomOf(s, uid);
  return wait({
    documents: findDocs(mine, by),
    total: mine.length,
    others: [...others.values()].sort((a, b) => a.name.localeCompare(b.name, 'nb')),
    /* What this firm has asked for and not been given yet. Only its own
       questions: an invitation another firm sent is between them and the
       client. */
    pending: room.invites
      .filter(i => i.state === 'pending' && i.by === businessId)
      .map(i => ({
        id: i.id,
        who: i.who,
        name: nameOfId(i.who),
        about: i.about,
        at: i.at,
        count: i.docs ? i.docs.length : mine.length,
      })),
  });
}

/**
 * A paper goes in.
 *
 * `by` is who put it there — 'customer' or a business id — and the firm
 * currently engaged is granted access as part of the same act, because a
 * document the client sends to their lawyer that the lawyer cannot open is
 * not a document they have sent.
 */
export function addToRoom(phone, businessId, file, by = 'customer') {
  const uid = normalise(phone);
  if (!uid) throw new Error('No client.');
  const business = catalogue().find(x => x.id === businessId);
  if (!business) throw new Error('Unknown business.');

  const name = String(file?.name || '').trim();
  if (!name) throw new Error('That file has no name.');
  const size = Number(file?.size) || 0;
  if (size > 25 * 1024 * 1024) throw new Error('That file is larger than 25 MB.');

  const s = state();
  const room = roomOf(s, uid);
  const doc = {
    id: newId('D'),
    name,
    size,
    note: String(file?.note || '').trim() || null,
    kind: file?.kind || null,
    by,
    businessId,
    at: new Date().toISOString(),
    /* The file itself is not here. There is no document store behind this
       build, and keeping the contents of somebody's contract in localStorage
       would be neither durable nor private. */
    stored: false,
    access: grantAccess([], businessId, 'engaged'),
  };
  room.documents.unshift(doc);
  note(s, uid, { what: 'added', doc: doc.id, docName: name, by: by === 'customer' ? uid : by });
  write(s);
  return fetchRoom(uid);
}

/**
 * Invite somebody to work on the case.
 *
 * Grants access across the whole room, or to named documents. Nothing is
 * copied: the co-counsel is reading the client's papers, in the client's
 * room, and the client can see that they are.
 */
export function inviteToRoom(phone, who, { docs, role = 'cocounsel', by, about } = {}) {
  const uid = normalise(phone);
  if (!who) throw new Error('Nobody to invite.');
  if (who === by) throw new Error('You are already on this case.');
  if (!catalogue().some(x => x.id === who)) throw new Error('Unknown firm.');
  const s = state();
  const room = roomOf(s, uid);

  if (room.invites.some(i => i.who === who && i.state === 'pending'))
    throw new Error('They have already been asked, and the client has not answered yet.');

  /* **Nothing is granted here.** The invitation is a REQUEST, and it sits
     pending until the client answers it.

     This is the one rule the whole platform is built on, applied to the
     thing it is easiest to forget it applies to: a firm cannot make somebody
     else's papers readable by a third party, however sensible the reason,
     because the papers are not the firm's. Today a lawyer forwards the
     bundle to a colleague and the client learns about it never. Here they
     are asked, and the asking is on their record whichever way they answer.

     It is also what keeps the platform rule intact rather than special-cased:
     the invited firm becomes a firm the client has engaged, by the client's
     own act, and is disclosed to on exactly the same terms as any other. */
  room.invites.unshift({
    id: newId('I'),
    who,
    by: by || null,
    about: String(about || '').trim() || null,
    role,
    docs: docs?.length ? [...docs] : null,        /* null = the whole room */
    at: new Date().toISOString(),
    state: 'pending',
  });

  note(s, uid, {
    what: 'asked', who, by, about: about || null,
    scope: docs?.length ? `${docs.length} documents` : 'the whole room',
  });
  write(s);
  return fetchRoom(uid);
}

/**
 * The client answers.
 *
 * Yes grants the access the invitation asked for and makes the firm one the
 * client has engaged — a relation made by the customer, because that is what
 * just happened. No closes the invitation and grants nothing. Either way the
 * question and the answer stay on the record.
 */
export function respondToInvite(phone, inviteId, yes) {
  const uid = normalise(phone);
  const s = state();
  const room = roomOf(s, uid);
  const inv = room.invites.find(i => i.id === inviteId);
  if (!inv) throw new Error('That invitation is not here.');
  if (inv.state !== 'pending') throw new Error('That has already been answered.');

  inv.state = yes ? 'accepted' : 'declined';
  inv.answered = new Date().toISOString();

  if (yes) {
    const pick = d => !inv.docs || inv.docs.includes(d.id);
    const extra = { by: inv.by, about: inv.about };
    room.documents = room.documents.map(d => (pick(d)
      ? { ...d, access: grantAccess(d.access, inv.who, inv.role, new Date(), extra) }
      : d));

    /* The relation the client just made. `by: 'customer'` is not a
       convenience — they answered the question themselves, so the ordinary
       disclosure rule applies and no exception has to be carved for
       co-counsel. */
    const firm = catalogue().find(x => x.id === inv.who);
    s.relations[uid] = s.relations[uid] || [];
    if (firm && !s.relations[uid].some(r => r.businessId === inv.who)) {
      s.relations[uid].push({
        businessId: inv.who,
        industry: firm.industry,
        since: new Date().toISOString().slice(0, 10),
        number: new Date().getFullYear() + '-' + (1000 + s.relations[uid].length * 7 + 3),
        by: 'customer',
        role: 'cocounsel',
        invitedBy: inv.by,
      });
    }
  }

  note(s, uid, { what: yes ? 'accepted' : 'declined', who: inv.who, by: uid });
  write(s);
  return fetchRoom(uid);
}

/** And take it away again. The row stays, closed. */
export function removeFromRoom(phone, who, { by } = {}) {
  const uid = normalise(phone);
  const s = state();
  const room = roomOf(s, uid);

  /* The engaged firm does not leave by this door. Ending the engagement is a
     transfer — it has to say where the case is going — and a button that
     could quietly close the only live access would leave a client with an
     archive nobody is working on and no record of why. */
  const engaged = room.documents.some(d => (d.access || [])
    .some(g => g.who === who && !g.until && g.role === 'engaged'));
  if (engaged) throw new Error('That is the firm on the case. Hand the case on instead.');

  /* A question withdrawn before it was answered. It stays on the record as
     withdrawn rather than vanishing — the client was asked, and that happened
     whether or not anybody waited for the answer. */
  room.invites = room.invites.map(i => (i.who === who && i.state === 'pending'
    ? { ...i, state: 'withdrawn', answered: new Date().toISOString() }
    : i));

  room.documents = room.documents.map(d => ({ ...d, access: revokeAccess(d.access, who) }));
  note(s, uid, { what: 'removed', who, by });
  write(s);
  return fetchRoom(uid);
}

/**
 * Hand the case to another firm.
 *
 * The outgoing firm's access closes, the incoming firm's opens, and not one
 * document moves — which is what makes "the papers follow the client" true
 * rather than a slogan. The relation follows too, so the new firm's CRM has
 * the client and the old one's stops having them.
 */
export function transferCase(phone, fromId, toId, { by } = {}) {
  const uid = normalise(phone);
  if (fromId === toId) throw new Error('That is the same firm.');
  const to = catalogue().find(x => x.id === toId);
  if (!to) throw new Error('Unknown firm.');

  const s = state();
  const room = roomOf(s, uid);
  room.documents = room.documents.map(d => ({
    ...d, access: transferAccess(d.access, fromId, toId),
  }));

  /* Questions the outgoing firm asked and nobody answered go with it. A
     client should not be left holding an invitation from a firm that is no
     longer on the case. */
  room.invites = room.invites.map(i => (i.by === fromId && i.state === 'pending'
    ? { ...i, state: 'withdrawn', answered: new Date().toISOString() }
    : i));

  /* The relation moves with it. A client whose case has gone to another firm
     is not still a client of the first one, and leaving the row behind would
     leave them in a CRM they have left. */
  s.relations[uid] = (s.relations[uid] || []).map(r => (r.businessId === fromId
    ? { ...r, businessId: toId, industry: to.industry, transferredFrom: fromId }
    : r));

  note(s, uid, { what: 'transferred', who: toId, from: fromId, by });
  write(s);
  return fetchRoom(uid);
}

/**
 * Send a paper out of the room, to somebody who is not in it.
 *
 * Chamcore's `forwardDocumentCenter` emails the file and logs the act. The
 * logging is the part that matters and is kept: a document that has left the
 * room has left it, and the client is entitled to know that it did, to whom,
 * and when.
 */
export function forwardFromRoom(phone, docId, to, { by } = {}) {
  const uid = normalise(phone);
  const s = state();
  const room = roomOf(s, uid);
  const doc = room.documents.find(d => d.id === docId);
  if (!doc) throw new Error('No such document.');
  const where = String(to?.email || '').trim();
  if (!where) throw new Error('Where should it go?');

  note(s, uid, {
    what: 'forwarded', doc: doc.id, docName: doc.name, to: where,
    toName: String(to?.name || '').trim() || null, by,
    /* Nothing is sent: no mail server. The record of the act is real. */
    demo: true,
  });
  write(s);
  return fetchRoom(uid);
}
